import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import AndroidAppBanner from '../components/AndroidAppBanner';
import IOSInstallBanner from '../components/IOSInstallBanner';
import { ProcessingCardOverlay } from '../components/ProcessingIndicator';
import { InFeedAd } from '../components/AdSense';
import { ReminderBadge } from '../components/ReminderPicker';
import { ReminderAlerts } from '../components/ReminderAlert';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { requestPushPermission } from '../lib/pushPermission';
import { BackToAdminButton } from '../components/ImpersonationButtons';
import { useBiometric } from '../hooks/useBiometric';
import { useReminderPolling } from '../hooks/useReminderPolling';
import AddBookmarkModal from '../components/AddBookmarkModal';
import QuickCaptureButtons from '../components/QuickCaptureButtons';
import { UpgradeModal } from '../components/UpgradeModal';
import QuotaThresholdNudge from '../components/QuotaThresholdNudge';
import AIUsageStatsModal from '../components/AIUsageStatsModal';
import ShareModal from '../components/ShareModal';
import ReferralsModal from '../components/ReferralsModal';
import { FeedbackPill } from '../components/FeedbackPill';
import { track, trackServer } from '../lib/analytics';
import { SidebarProfileCard } from '../components/SidebarProfileCard';
import { UsernameSettingsModal } from '../components/UsernameSettingsModal';
import { ShareDemoRefresherModal } from '../components/ShareDemoRefresherModal';
import FirstRunWelcome from '../components/FirstRunWelcome';
import { ClaimPathWelcome } from './dashboard/ClaimPathWelcome';
import { FLAGS } from '../lib/flags';
import BrainSyncLoader from '../components/BrainSyncLoader';
import { ProBadge } from '../components/ProBadge';
import OnboardingOverlay from '../components/OnboardingOverlay';
import { readDashboardCache, writeDashboardCache, clearDashboardCache } from '../utils/dashboardCache';
import { readAiQueries, saveAiQuery, clearAiQueries } from '../lib/search-history';
import { useVoiceInput } from '../hooks/useVoiceInput';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Toaster, toast } from 'sonner';
import ShareCategoryModal from '../components/ShareCategoryModal';
import { 
    Bookmark, Search, Plus, Play, FileText, Image as ImageIcon, 
    Link as LinkIcon, Link2, Loader2, MoreVertical, Trash2, RefreshCw,
    Filter, Tag, LogOut, User, ExternalLink, Clock, Sparkles,
    Edit2, StickyNote, FolderPlus, Menu, X, ChevronRight,
    Cpu, Briefcase, GraduationCap, Film, Heart, Coffee, Newspaper,
    Zap, UtensilsCrossed, Plane, Brain, AlertTriangle, ArrowUpDown, Share2,
    Type, Download,
    Music, Camera, BookOpen, Home, Globe, ShoppingBag, Palette, Code,
    Dumbbell, MessageCircle, Car, Baby, Dog, Gamepad2, Landmark, Stethoscope, Settings,
    Users, Bell, Fingerprint,
    BrainCircuit, TrendingUp, Clapperboard, HeartPulse, Target, ChefHat, MapPin, Compass,
    Scale, Wine, Lightbulb, Flame, Flag, Trophy, Mic, Tv, Star, Rocket,
} from 'lucide-react';
import {
    DndContext,
    PointerSensor,
    TouchSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    closestCenter,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    rectSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import { CATEGORY_ICONS, CATEGORY_COLORS, ICON_OPTIONS } from './dashboard/constants';
import { SortableCard } from './dashboard/SortableCard';
import { BookmarkCard } from './dashboard/BookmarkCard';
import { FiltersBar } from './dashboard/FiltersBar';
import { CategorySidebar } from './dashboard/CategorySidebar';
import { DashboardHeader } from './dashboard/DashboardHeader';
import { CategoryModal } from './dashboard/CategoryModal';
import { DeleteCategoryDialog } from './dashboard/DeleteCategoryDialog';
import {
    isHebrew, formatDate, getContentIcon, hasQualityIssues,
    getQualityMessage, getStatusBadge, getCategoryIcon,
    looksLikeNaturalLanguage,
} from './dashboard/utils';
import { useBrandScrollProgress } from './dashboard/hooks/useBrandScrollProgress';
import { useNotifications } from './dashboard/hooks/useNotifications';
import { useBookmarkReminders } from './dashboard/hooks/useBookmarkReminders';

const API = '/api';

const Dashboard = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, logout, login, loading: authLoading, checkAuth } = useAuth();
    // Scroll-aware brand fade — extracted to `useBrandScrollProgress`.
    const brandScrollProgress = useBrandScrollProgress();
    // Static tagline under the wordmark - personal greetings were
    // dropped per UX request (Feb 28, 2026).
    const headerGreeting = 'Your AI Second Brain';
    // Read the persistent cache exactly once per mount. If present, this
    // makes the dashboard appear instantly on TWA cold-resume.
    const hydrated = useRef(readDashboardCache());
    const [bookmarks, setBookmarks] = useState(() => hydrated.current?.bookmarks || []);
    const [categories, setCategories] = useState(() => hydrated.current?.categories || []);
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(() => !hydrated.current);
    const [searchQuery, setSearchQuery] = useState(() => {
        return sessionStorage.getItem('dashboard_search') || '';
    });
    const [selectedTag, setSelectedTag] = useState(null);
    const [selectedType, setSelectedType] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(() => {
        // When the user opens a bookmark from inside a collection and
        // then hits Back, the browser navigates to /dashboard with no
        // URL params - we'd lose the scope and dump them into "All
        // Bookmarks". `navigateToBookmark` saves the active scope to
        // sessionStorage before navigating; we restore-and-consume it
        // here so the dashboard reopens on the same collection.
        try {
            const v = sessionStorage.getItem('dashboard_selectedCategory');
            sessionStorage.removeItem('dashboard_selectedCategory');
            return v || null;
        } catch (_e) {
            return null;
        }
    });
    const [selectedSubcategory, setSelectedSubcategory] = useState(() => {
        try {
            const v = sessionStorage.getItem('dashboard_selectedSubcategory');
            sessionStorage.removeItem('dashboard_selectedSubcategory');
            return v || null;
        } catch (_e) {
            return null;
        }
    });
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [addModalInitialUrl, setAddModalInitialUrl] = useState('');
    const [addModalInitialMode, setAddModalInitialMode] = useState('url');
    const [addModalInitialFile, setAddModalInitialFile] = useState(null);
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    // The search bar is regex-only now. Any "AI search" flow (Enter key
    // or the escalation chip) navigates to /search?q=... which reuses
    // the /Ask-AI page's prose+sources UX. This replaces the earlier
    // inline `/bookmarks/ai-search` filter approach, which suffered
    // from poor recall on real production libraries and dumped users
    // into the onboarding empty-state when the AI returned zero hits.
    // Recent AI queries surfaced as chips under an empty search bar.
    const [aiQueryHistory, setAiQueryHistory] = useState([]);
    // Only show the recent-searches row while the input is focused —
    // permanent rendering felt cluttered and pushed real content down.
    // `searchFocused` flips on/off via onFocus/onBlur, with a tiny
    // timeout on blur so tapping a chip has time to fire before the
    // row disappears.
    const [searchFocused, setSearchFocused] = useState(false);
    const [sortBy, setSortBy] = useState('created_desc');
    const searchTimerRef = useRef(null);
    const fileInputRef = useRef(null);
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Load persisted recent AI queries when user is known.
    useEffect(() => {
        if (user?.user_id) setAiQueryHistory(readAiQueries(user.user_id));
    }, [user?.user_id]);

    // Single choke-point for every "Ask BrainSync" hand-off. Persists
    // the query to the recent-searches list and navigates to /search.
    // Called from: search-bar Enter, inline Brain shortcut, escalation
    // chip, empty-state Ask BrainSync button, history chip clicks, and
    // the voice-input hook when recognition finishes.
    const handleAiSearch = useCallback((rawQuery) => {
        const q = (rawQuery || '').trim();
        if (q && user?.user_id) {
            saveAiQuery(user.user_id, q);
            setAiQueryHistory(readAiQueries(user.user_id));
        }
        navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
    }, [navigate, user?.user_id]);

    // Voice input for the search bar. Tapping the mic starts a Web
    // Speech API session; the recognized text streams into the input
    // as the user speaks, and when the recognizer stops (natural
    // pause) we auto-navigate to /search with the transcript. On
    // unsupported browsers the button is hidden entirely.
    //
    // Language auto-detect: browsers default the recognizer to their
    // UI locale, which is usually en-US regardless of what the user
    // is about to say. Passing `navigator.language` (e.g. "he-IL",
    // "th-TH", "ja-JP") gives the recognizer the right acoustic +
    // vocabulary model, dramatically improving transcription accuracy
    // for Hebrew / Thai / Japanese users saving content in those
    // languages. Falls back to browser default when unavailable.
    const voice = useVoiceInput({
        lang: (typeof navigator !== 'undefined' && navigator.language) || undefined,
    });
    useEffect(() => {
        if (voice.transcript) setSearchQuery(voice.transcript);
    }, [voice.transcript]);
    const wasListeningRef = useRef(false);
    useEffect(() => {
        // Fires when the recognizer transitions from listening → not.
        if (wasListeningRef.current && !voice.listening) {
            const q = voice.transcript.trim();
            if (q) handleAiSearch(q);
        }
        wasListeningRef.current = voice.listening;
    }, [voice.listening, voice.transcript, handleAiSearch]);
    const [deleteCategoryId, setDeleteCategoryId] = useState(null);
    const [shareCategoryOpen, setShareCategoryOpen] = useState(false);
    const [shareCategoryTarget, setShareCategoryTarget] = useState(null);
    const [showFavorites, setShowFavorites] = useState(false);
    const [nextCursor, setNextCursor] = useState(() => hydrated.current?.nextCursor || null);
    const [hasMore, setHasMore] = useState(() => !!hydrated.current?.hasMore);
    const [loadingMore, setLoadingMore] = useState(false);
    const loadMoreRef = useRef(null);
    const [totalCount, setTotalCount] = useState(() => hydrated.current?.totalCount || 0);
    // Ref mirror of totalCount so callbacks (like `handleBookmarkAdded`)
    // can guard on the live count without needing it in their deps array
    // (adding it would recreate the callback on every save and cascade
    // extra child re-renders through AddBookmarkModal / MobileAddSheet).
    const totalCountRef = useRef(totalCount);
    useEffect(() => { totalCountRef.current = totalCount; }, [totalCount]);
    const [favoritesCount, setFavoritesCount] = useState(() => hydrated.current?.favoritesCount || 0);
    const restoredFromCache = useRef(false);
    const [extensionBannerDismissed, setExtensionBannerDismissed] = useState(() => localStorage.getItem('bs_ext_banner_dismissed') === '1');
    // Real-time extension detection via the content script marker
    // (v1.1.2+). The content script writes `window.__brainsync_extension`
    // at `document_start` on every brainsync.pro page load; we read
    // that here on mount (and on the `brainsync-extension-ready`
    // event in case the marker races React's initial render).
    const [extensionDetectedInPage, setExtensionDetectedInPage] = useState(() => {
        try { return !!(window.__brainsync_extension && window.__brainsync_extension.installed); }
        catch (_) { return false; }
    });
    useEffect(() => {
        if (extensionDetectedInPage) return;
        const check = () => {
            try {
                if (window.__brainsync_extension && window.__brainsync_extension.installed) {
                    setExtensionDetectedInPage(true);
                    return true;
                }
            } catch (_) { /* ignore */ }
            return false;
        };
        if (check()) return;
        // A second check after mount catches the content-script race
        // where React ran before the extension injected the marker.
        const t = setTimeout(check, 300);
        const onReady = () => check();
        window.addEventListener('brainsync-extension-ready', onReady);
        return () => {
            clearTimeout(t);
            window.removeEventListener('brainsync-extension-ready', onReady);
        };
    }, [extensionDetectedInPage]);
    const isDesktopChrome = useMemo(() => {
        const ua = navigator.userAgent;
        const isChrome = /Chrome\//.test(ua) && !/Edg\/|OPR\//.test(ua);
        const isMobileUA = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
        const isTWA = window.matchMedia('(display-mode: standalone)').matches || document.referrer.includes('android-app://');
        return isChrome && !isMobileUA && !isTWA;
    }, []);
    const bookmarkReminders = useBookmarkReminders(user);
    const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
    const [aiUsageStatsOpen, setAiUsageStatsOpen] = useState(false);
    const [subscriptionInfo, setSubscriptionInfo] = useState(() => (
        hydrated.current?.subscriptionInfo
        || { tier: user?.tier || 'free', bookmark_limit: user?.bookmark_limit || 30, bookmark_count: 0 }
    ));

    // Notifications (bell + unread count + dropdown) — extracted hook.
    const { unreadCount, notifications, notifOpen, handleOpen: handleOpenNotifications, close: closeNotifications } = useNotifications(user);

    // Register for push notifications
    usePushNotifications(user);
    const biometric = useBiometric();

    // Poll for due reminders (in-app alerts)
    const { dueReminders, dismissReminder, snoozeReminder } = useReminderPolling(user);

    // Fetch active reminders to show badges on cards — handled by
    // useBookmarkReminders hook (declared above near other user-scoped state).

    // Fetch subscription status
    const fetchSubscriptionStatus = useCallback(async () => {
        if (!user) return;
        try {
            const res = await fetch(`${API}/subscriptions/status`, { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                setSubscriptionInfo(data);
            }
        } catch (err) { console.error('Failed to fetch subscription:', err); }
    }, [user]);

    useEffect(() => { fetchSubscriptionStatus(); }, [fetchSubscriptionStatus]);

    // Handle Stripe checkout return - poll for payment status
    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        const subParam = searchParams.get('subscription');
        if (!sessionId || subParam !== 'success') return;

        // Clean URL
        window.history.replaceState({}, '', '/dashboard');

        let attempts = 0;
        const poll = async () => {
            try {
                const res = await fetch(`${API}/subscriptions/checkout/status/${sessionId}`, { credentials: 'include' });
                if (res.ok) {
                    const data = await res.json();
                    if (data.payment_status === 'paid') {
                        toast.success('Subscription activated! Welcome to Pro.');
                        fetchSubscriptionStatus();
                        checkAuth({ silent: true });
                        return;
                    }
                }
            } catch (err) { console.error('Bookmark poll error:', err); }
            if (attempts < 5) setTimeout(poll, 2000);
        };
        poll();
    }, [searchParams, fetchSubscriptionStatus, checkAuth]);

    // Handle limit-reached callback from AddBookmarkModal
    const handleLimitReached = useCallback((detail) => {
        setSubscriptionInfo(prev => ({
            ...prev,
            bookmark_count: detail?.current_count ?? prev.bookmark_count,
            bookmark_limit: detail?.bookmark_limit ?? prev.bookmark_limit,
            tier: detail?.tier ?? prev.tier,
        }));
        setUpgradeModalOpen(true);
    }, []);


    // Handle ?category= param from join redirect - select the shared category on arrival
    useEffect(() => {
        const categoryParam = searchParams.get('category');
        if (categoryParam) {
            setSelectedCategory(categoryParam);
            window.history.replaceState({}, '', '/dashboard');
        }
    }, [searchParams, setSelectedCategory]);

    // Memoize parent categories to avoid recalculating in every render
    const parentCategories = useMemo(() => categories.filter(c => !c.parent_id), [categories]);
    const ownedParentCategories = useMemo(() => parentCategories.filter(c => !c.my_role), [parentCategories]);
    const sharedWithMeCategories = useMemo(() => parentCategories.filter(c => c.my_role), [parentCategories]);


    // Debounce search input. Uniform 250ms delay - regex-first means
    // the fast path always fires within a keystroke, and the AI path
    // is gated on an explicit user action (Enter / chip tap) so we
    // don't need to slow the debounce down to protect the LLM.
    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 250);
        // Persist search query for back navigation
        sessionStorage.setItem('dashboard_search', searchQuery);
        return () => clearTimeout(searchTimerRef.current);
    }, [searchQuery]);
    
    // Category form state
    const [categoryName, setCategoryName] = useState('');
    const [categoryColor, setCategoryColor] = useState('#8B5CF6');
    const [categoryIcon, setCategoryIcon] = useState('folder');
    const [categoryParentId, setCategoryParentId] = useState('');
    const [categoryDescription, setCategoryDescription] = useState('');
    const [categoryManualOrder, setCategoryManualOrder] = useState(false);
    const [categoryShowNumbering, setCategoryShowNumbering] = useState(false);
    const [categoryShareVisibility, setCategoryShareVisibility] = useState('private');
    // Icon picker collapsed by default in the modal - most edits
    // change just the name or description, so hiding the (tall) grid
    // behind a disclosure trigger keeps the modal short. Re-opens
    // automatically when the user is creating a brand-new collection
    // (icon-choice IS a primary act there).
    const [iconPickerOpen, setIconPickerOpen] = useState(false);
    const [colorPickerOpen, setColorPickerOpen] = useState(false);

    const fetchBookmarksRef = useRef(null);
    const nextCursorRef = useRef(hydrated.current?.nextCursor || null);
    // Token bumped on every filter change so we can discard stale fetches
    const fetchTokenRef = useRef(0);

    // NOTE: Notification permission is now requested in user gesture context
    // via ensurePushSubscription() when user clicks a reminder preset button.
    // This avoids the Android TWA issue where permission prompts are blocked
    // when called outside of user gesture context.

    const fetchBookmarks = useCallback(async (loadMore = false) => {
        // For a fresh fetch (not load-more), invalidate any in-flight fetches
        // by bumping the token, then capture our own. This is the single
        // authoritative point where staleness is decided - no separate reset
        // effect needed.
        if (!loadMore) {
            fetchTokenRef.current += 1;
        }
        const myToken = fetchTokenRef.current;
        const isStale = () => myToken !== fetchTokenRef.current;
        try {
            // Search bar is regex-only now. AI queries navigate to
            // /search?q=... (see search-input Enter handler / chip
            // click). This keeps the fetch path predictable, fast, and
            // free of LLM-fallback edge cases.
            const params = new URLSearchParams();
            if (debouncedSearch) params.append('search', debouncedSearch);
            if (selectedTag) params.append('tag', selectedTag);
            if (selectedType) params.append('content_type', selectedType);
            if (selectedCategory) params.append('category_id', selectedCategory);
            if (showFavorites) params.append('favorite', 'true');
            params.append('limit', '20');
            if (loadMore && nextCursorRef.current) params.append('cursor', nextCursorRef.current);

            const response = await fetch(`${API}/bookmarks?${params.toString()}`, {
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                if (isStale()) return;
                const items = data.bookmarks || data;
                if (loadMore) {
                    // Dedup by bookmark_id - prevents duplicate cards when the
                    // IntersectionObserver fires twice with the same cursor or
                    // when a stale loadMore request lands after a category change.
                    setBookmarks(prev => {
                        const seen = new Set(prev.map(b => b.bookmark_id));
                        const unique = items.filter(b => !seen.has(b.bookmark_id));
                        return [...prev, ...unique];
                    });
                } else {
                    setBookmarks(items);
                }
                setHasMore(data.has_more || false);
                setNextCursor(data.next_cursor || null);
                nextCursorRef.current = data.next_cursor || null;
                if (data.total_count !== undefined) setTotalCount(data.total_count);
                if (data.favorites_count !== undefined) setFavoritesCount(data.favorites_count);
            } else if (response.status === 401) {
                const recovered = await checkAuth({ silent: true, maxRetries: 1 });
                if (!recovered) navigate('/');
            }
        } catch (error) {
            // Bookmarks fetch failed silently
        } finally {
            if (!isStale()) {
                setLoading(false);
                setLoadingMore(false);
            }
        }
    }, [debouncedSearch, selectedTag, selectedType, selectedCategory, showFavorites, navigate, checkAuth]);

    // Keep ref in sync so polling uses latest fetch without re-creating interval
    useEffect(() => { fetchBookmarksRef.current = fetchBookmarks; }, [fetchBookmarks]);

    const fetchCategories = useCallback(async () => {
        try {
            const response = await fetch(`${API}/categories`, {
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setCategories(data);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    }, []);

    const fetchTags = useCallback(async () => {
        try {
            const response = await fetch(`${API}/tags`, {
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setTags(data);
            }
        } catch (error) {
            console.error('Error fetching tags:', error);
        }
    }, []);

    // Initial load - fetch categories and tags once
    useEffect(() => {
        if (!authLoading && !user) {
            // Before bouncing to landing, give the WebView one more chance
            // to send up the session cookie. On TWA cold-start, the
            // CookieStore sometimes hasn't propagated the cookie by the
            // time AuthContext's first /api/auth/me fires, so we'd flash
            // the marketing landing for a previously-signed-in user.
            // `brainsync_user_email` is cleared on real logout, so its
            // presence is a strong "this user was here recently" signal.
            const savedEmail = (() => {
                try { return localStorage.getItem('brainsync_user_email'); } catch (_) { return null; }
            })();
            if (savedEmail) {
                (async () => {
                    for (let i = 0; i < 4; i++) {
                        const recovered = await checkAuth({
                            silent: true,
                            maxRetries: 1,
                            timeoutMs: 6000,
                        });
                        if (recovered) return;
                        await new Promise(r => setTimeout(r, 700));
                    }
                    // Genuine logout - drop the marker and go to landing.
                    try { localStorage.removeItem('brainsync_user_email'); } catch (_) {}
                    clearDashboardCache();
                    navigate('/');
                })();
                return;
            }
            // No marker → fresh device or post-logout. Straight to landing.
            clearDashboardCache();
            navigate('/');
            return;
        }
        if (user) {
            // Defensive: if the hydrated cache belongs to a different
            // account than the one the cookie just resolved to, throw it
            // away so we don't show the wrong user's bookmarks.
            if (hydrated.current && hydrated.current.email && hydrated.current.email !== user.email) {
                hydrated.current = null;
                setBookmarks([]);
                setCategories([]);
                setNextCursor(null);
                setHasMore(false);
                setTotalCount(0);
                setFavoritesCount(0);
                setLoading(true);
                clearDashboardCache();
            }
            fetchCategories();
            fetchTags();
        }
    }, [user, authLoading, fetchCategories, fetchTags, navigate, checkAuth]);

    // Persist a snapshot of the default-view dashboard state to localStorage
    // so cold TWA resumes can hydrate instantly. We only cache the unfiltered
    // view - once the user starts filtering/searching we leave the previous
    // snapshot intact for next launch.
    useEffect(() => {
        if (!user || loading) return;
        const isDefaultView = !debouncedSearch && !selectedTag && !selectedType && !selectedCategory && !showFavorites;
        if (!isDefaultView) return;
        writeDashboardCache({
            email: user.email,
            bookmarks,
            categories,
            totalCount,
            favoritesCount,
            nextCursor,
            hasMore,
            subscriptionInfo,
        });
    }, [user, loading, bookmarks, categories, totalCount, favoritesCount, nextCursor, hasMore, subscriptionInfo, debouncedSearch, selectedTag, selectedType, selectedCategory, showFavorites]);

    // Fetch bookmarks when filters change
    useEffect(() => {
        if (user) {
            // On first mount, restore cached bookmarks from back-navigation
            if (!restoredFromCache.current) {
                restoredFromCache.current = true;
                // IDs that BookmarkDetail deleted just before navigating back.
                let deletedIds = [];
                try {
                    deletedIds = JSON.parse(sessionStorage.getItem('dashboard_deleted_ids') || '[]');
                } catch (e) { /* ignore */ }
                sessionStorage.removeItem('dashboard_deleted_ids');

                const cached = sessionStorage.getItem('dashboard_owner') === user.user_id
                    ? sessionStorage.getItem('dashboard_bookmarks')
                    : null;
                if (!cached) {
                    ['dashboard_bookmarks', 'dashboard_cursor', 'dashboard_hasMore'].forEach((k) => sessionStorage.removeItem(k));
                }
                sessionStorage.removeItem('dashboard_owner');
                if (cached) {
                    try {
                        const parsed = JSON.parse(cached);
                        if (Array.isArray(parsed) && parsed.length > 0) {
                            const filtered = deletedIds.length
                                ? parsed.filter(b => !deletedIds.includes(b.bookmark_id))
                                : parsed;
                            setBookmarks(filtered);
                            const savedCursor = sessionStorage.getItem('dashboard_cursor');
                            const savedHasMore = sessionStorage.getItem('dashboard_hasMore');
                            setNextCursor(savedCursor || null);
                            nextCursorRef.current = savedCursor || null;
                            setHasMore(savedHasMore === '1');
                            setLoading(false);
                            sessionStorage.removeItem('dashboard_bookmarks');
                            sessionStorage.removeItem('dashboard_cursor');
                            sessionStorage.removeItem('dashboard_hasMore');
                            // Refresh in the background so totals/newly processed items sync
                            if (deletedIds.length) setTimeout(() => fetchBookmarks(), 300);
                            return;
                        }
                    } catch (e) { console.warn('Cache parse error:', e); }
                    sessionStorage.removeItem('dashboard_bookmarks');
                    sessionStorage.removeItem('dashboard_cursor');
                    sessionStorage.removeItem('dashboard_hasMore');
                }
            }
            fetchBookmarks();
        }
    }, [user, fetchBookmarks]);

    // Restore scroll position after bookmarks load
    useEffect(() => {
        if (!loading && bookmarks.length > 0) {
            const saved = sessionStorage.getItem('dashboard_scroll');
            if (saved) {
                requestAnimationFrame(() => window.scrollTo(0, parseInt(saved, 10)));
                sessionStorage.removeItem('dashboard_scroll');
            }
        }
    }, [loading, bookmarks.length]);

    // Infinite scroll - auto-load more when sentinel enters viewport
    useEffect(() => {
        const sentinel = loadMoreRef.current;
        if (!sentinel) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
                    setLoadingMore(true);
                    fetchBookmarks(true);
                }
            },
            { rootMargin: '200px' }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, loading, loadingMore, fetchBookmarks]);

    const navigateToBookmark = (path) => {
        sessionStorage.setItem('dashboard_scroll', String(window.scrollY));
        try {
            sessionStorage.setItem('dashboard_bookmarks', JSON.stringify(bookmarks));
            sessionStorage.setItem('dashboard_owner', user?.user_id || '');
            sessionStorage.setItem('dashboard_cursor', nextCursorRef.current || '');
            sessionStorage.setItem('dashboard_hasMore', hasMore ? '1' : '0');
            // Preserve the active scope (category/subcategory) so a
            // browser-back from /bookmark/:id lands the user on the
            // SAME collection page they came from, not the global
            // "All Bookmarks" view.
            if (selectedCategory) {
                sessionStorage.setItem('dashboard_selectedCategory', selectedCategory);
            } else {
                sessionStorage.removeItem('dashboard_selectedCategory');
            }
            if (selectedSubcategory) {
                sessionStorage.setItem('dashboard_selectedSubcategory', selectedSubcategory);
            } else {
                sessionStorage.removeItem('dashboard_selectedSubcategory');
            }
        } catch (e) { console.warn('Session storage quota exceeded:', e); }
        navigate(path);
    };

    // Polling for processing bookmarks - lightweight endpoint, merges status updates into existing list
    const hasProcessingRef = useRef(false);
    useEffect(() => {
        hasProcessingRef.current = bookmarks.some(b => b.status === 'processing' || b.status === 'pending');
    }, [bookmarks]);

    useEffect(() => {
        const interval = setInterval(async () => {
            if (!hasProcessingRef.current) return;
            try {
                const response = await fetch(`${API}/bookmarks/processing`, { credentials: 'include' });
                if (!response.ok) return;
                const processing = await response.json();
                const processingIds = new Set(processing.map(b => b.bookmark_id));

                setBookmarks(prev => {
                    let changed = false;
                    const updated = prev.map(bm => {
                        // If this bookmark was processing and is no longer in the processing list, it completed
                        if ((bm.status === 'processing' || bm.status === 'pending') && !processingIds.has(bm.bookmark_id)) {
                            changed = true;
                            return null; // Mark for full refresh
                        }
                        // Update processing stage from lightweight poll
                        const match = processing.find(p => p.bookmark_id === bm.bookmark_id);
                        if (match && (match.processing_stage !== bm.processing_stage || match.status !== bm.status || match.title !== bm.title)) {
                            changed = true;
                            return { ...bm, ...match };
                        }
                        return bm;
                    });

                    // If any bookmark completed (null), do a full refresh
                    if (updated.includes(null)) {
                        const completed = prev.filter(bm =>
                            (bm.status === 'processing' || bm.status === 'pending') && !processingIds.has(bm.bookmark_id)
                        );

                        // Haptic confirmation tick - short-pause-short pattern.
                        // Doesn't fire if the page is currently visible AND we
                        // already just-toasted (prevents double-feedback on
                        // the same tab the user is staring at).
                        if (completed.length > 0) {
                            try { navigator.vibrate?.([20, 40, 20]); } catch (_) {}
                            // Toast confirmation - one toast per batch to avoid flooding
                            import('sonner').then(({ toast }) => {
                                if (completed.length === 1) {
                                    const bm = completed[0];
                                    toast.success(`"${bm.title || bm.url || 'Bookmark'}" is ready`, {
                                        duration: 3500,
                                    });
                                } else {
                                    toast.success(`${completed.length} bookmarks ready`, {
                                        duration: 3500,
                                    });
                                }
                            });
                        }

                        // Send browser notification for completed bookmarks
                        if ('Notification' in window && Notification.permission === 'granted') {
                            completed.forEach(bm => {
                                try {
                                    new Notification('BrainSync', {
                                        body: `"${bm.title || bm.url}" is ready!`,
                                        icon: '/logo192.png',
                                        tag: bm.bookmark_id
                                    });
                                } catch (e) { console.warn('Push notification failed:', e); }
                            });
                        }
                        fetchBookmarksRef.current?.();
                        return prev; // Return prev for now; full refresh will replace
                    }
                    return changed ? updated : prev;
                });
            } catch (e) {
                console.warn('Processing poll error:', e);
            }
        }, 5000);
        return () => clearInterval(interval);
    }, []); // Empty deps = interval created once


    const handleBookmarkAdded = useCallback((newBookmarkData) => {
        // Optimistically prepend the new bookmark so it appears immediately with processing status
        if (newBookmarkData?.bookmark_id) {
            // Fire `first_save` — the activation metric for the Google
            // Ads install campaign. Server is the source of truth: it
            // re-checks bookmarks.countDocuments({user_id}) and enforces
            // once-per-user via a unique index on (user_id, event_name).
            // We call it on EVERY save; the server dedupes. This fixes
            // three prior race conditions:
            //   1. Share-intent cold-start where `totalCountRef` hadn't
            //      hydrated when the save fired, so `=== 0` failed.
            //   2. localStorage stripped in private mode / TWA reinstall.
            //   3. Users saving via voice/manual endpoints that bypassed
            //      this handler entirely — server now catches those too.
            // Client-side ad-blockers can still block the gtag call, but
            // /api/analytics/event lands regardless (same-origin) so the
            // marketing dashboard sees every activation.
            try {
                trackServer('first_save', {
                    content_type: newBookmarkData?.content_type || undefined,
                });
            } catch (_) { /* analytics must never break the app */ }

            // Push permission ask right after the first successful save
            // (existing users: their next save). Helper enforces the 14-day cap.
            try {
                if (!localStorage.getItem('bs_push_first_save_asked')) {
                    setTimeout(() => {
                        requestPushPermission('first_save').then((result) => {
                            if (result !== 'skipped') {
                                try { localStorage.setItem('bs_push_first_save_asked', '1'); } catch (_) {}
                            }
                        });
                    }, 1500);
                }
            } catch (_) { /* never break the save flow */ }

            // Auto-open the brand-new bookmark IFF it's genuinely the
            // user's first (dashboard hadn't loaded any prior). Reading
            // the local ref is fine here — worst case is we skip the
            // auto-open coach-mark for a returning user, which is
            // exactly what we want.
            const isFirstSave = totalCountRef.current === 0;
            setBookmarks(prev => [newBookmarkData, ...prev.filter(b => b.bookmark_id !== newBookmarkData.bookmark_id)]);
            setTotalCount(prev => prev + 1);
            if (isFirstSave) {
                setTimeout(() => {
                    try {
                        navigate(`/bookmark/${newBookmarkData.bookmark_id}`);
                        track('first_save_auto_open', {
                            content_type: newBookmarkData?.content_type || undefined,
                        });
                    } catch (_) { /* nav must never break the save flow */ }
                }, 800);
            }
        }
        // Then refresh from server (will pick up completed status)
        setTimeout(() => fetchBookmarks(), 2000);
        fetchCategories();
        fetchTags();
        // Refresh quota and surface a short toast so new users understand the
        // Premium/Basic distinction at the moment of saving.
        fetchSubscriptionStatus().then(() => {
            try {
                if (newBookmarkData?.is_premium === false) {
                    toast.info('Saved as Basic - upgrade to add AI summaries', {
                        action: { label: 'Upgrade', onClick: () => setUpgradeModalOpen(true) },
                    });
                } else if (newBookmarkData?.is_premium === true && subscriptionInfo.premium_limit && subscriptionInfo.premium_limit !== -1) {
                    // Small heads-up only for the first few saves, then let the
                    // progress bar do the talking so we don't spam the user.
                    const used = (subscriptionInfo.premium_used ?? 0) + 1;
                    const remaining = Math.max(0, subscriptionInfo.premium_limit - used);
                    if (used <= 3 || remaining <= 5) {
                        toast.success(`Saved with AI - ${remaining} of ${subscriptionInfo.premium_limit} Premium saves left`);
                    }

                    // Halfway invite nudge - fires ONCE per user when they've
                    // used 50% of their Premium quota. Fires at 10/20 for Free
                    // and 100/200 for Pro 200. Contextual growth prompt right
                    // when scarcity starts to bite.
                    const nudgeKey = user?.user_id ? `bs_halfway_nudge_${user.user_id}` : null;
                    const already = nudgeKey ? localStorage.getItem(nudgeKey) : '1';
                    const halfway = Math.floor(subscriptionInfo.premium_limit / 2);
                    if (!already && subscriptionInfo.tier !== 'pro_unlimited' && used >= halfway) {
                        try { localStorage.setItem(nudgeKey, '1'); } catch (_) {}
                        toast('You\'re halfway through your Premium saves', {
                            description: 'Invite a friend - you both get +10 Premium AI saves.',
                            duration: 8000,
                            action: { label: 'Invite', onClick: () => setReferralsModalOpen(true) },
                        });
                    }
                }
            } catch (_) { /* silent */ }
        });
        setAddModalInitialUrl('');
    }, [fetchBookmarks, fetchCategories, fetchTags, fetchSubscriptionStatus, subscriptionInfo.premium_limit, subscriptionInfo.premium_used, subscriptionInfo.tier, user?.user_id]);

    // Mount-time halfway nudge - fires once for users who ALREADY crossed the
    // 50% mark before we shipped this feature (or before they added a new save).
    useEffect(() => {
        if (!user?.user_id) return;
        const limit = subscriptionInfo.premium_limit;
        if (!limit || limit === -1) return;
        if (subscriptionInfo.tier === 'pro_unlimited') return;
        const used = subscriptionInfo.premium_used ?? 0;
        const halfway = Math.floor(limit / 2);
        // Only fire when they still have quota left - a user at 100% gets the
        // quota-full UI, not this nudge.
        if (used < halfway || used >= limit) return;
        const nudgeKey = `bs_halfway_nudge_${user.user_id}`;
        if (localStorage.getItem(nudgeKey)) return;
        try { localStorage.setItem(nudgeKey, '1'); } catch (_) {}
        // Delay a beat so the toast doesn't fight with onboarding / initial loaders
        const t = setTimeout(() => {
            toast('You\'re halfway through your Premium saves', {
                description: 'Invite a friend - you both get +10 Premium AI saves.',
                duration: 8000,
                action: { label: 'Invite', onClick: () => setReferralsModalOpen(true) },
            });
        }, 2500);
        return () => clearTimeout(t);
    }, [user?.user_id, subscriptionInfo.premium_limit, subscriptionInfo.premium_used, subscriptionInfo.tier]);

    // (The old dashboard-level "Ask BrainSync" nudge lived here. It was
    // replaced by a coach-mark rendered inside the BookmarkDetail page
    // next to the AskBookmarkInline trigger — closer to the actual
    // feature so the pointer is contextual, not a top-level toast.)

    // Play Store review nudge - fires once per user, only for TWA
    // installs (Android app), only after they've saved 10 bookmarks
    // (past-the-fold engagement). Opens the Play Store listing in a
    // new window so the user can rate 5 stars while the "aha" is warm.
    // Deliberately doesn't use Google Play's In-App Review API because
    // that requires native TWA changes - the AAB stays untouched per
    // the campaign brief. Web-based rating dialog is the pragmatic path.
    useEffect(() => {
        if (!user?.user_id) return;
        if (totalCount < 10) return;
        // Only TWA users - reviewing a web app on Play Store is confusing.
        const isTwa = (() => {
            try {
                return window.matchMedia('(display-mode: standalone)').matches
                    || document.referrer.includes('android-app://');
            } catch (_) { return false; }
        })();
        if (!isTwa) return;
        const key = `bs_play_review_nudge:${user.user_id}`;
        if (localStorage.getItem(key)) return;
        try { localStorage.setItem(key, '1'); } catch (_) { /* quota */ }
        const t = setTimeout(() => {
            const PLAY_URL = 'https://play.google.com/store/apps/details?id=com.brainsync.myapp';
            toast('Enjoying BrainSync?', {
                description: 'A quick 5-star review helps other people find us. Takes 20 seconds.',
                duration: 12000,
                action: {
                    label: 'Rate us',
                    onClick: () => {
                        try { track('play_review_nudge_clicked'); } catch (_) { /* swallow */ }
                        // `market://` gets promoted to the native Play Store app on
                        // Android; falls through to the https URL for anyone who
                        // somehow lands here without the store installed.
                        window.open(PLAY_URL, '_blank', 'noopener,noreferrer');
                    },
                },
            });
            try { track('play_review_nudge_shown'); } catch (_) { /* swallow */ }
        }, 3500);
        return () => clearTimeout(t);
    }, [user?.user_id, totalCount]);

    // Handle share target (when someone shares a URL or file to BrainSync from another app).
    // One-shot: once we've consumed the share params, clear them via react-router's
    // setSearchParams so this effect doesn't re-fire when `handleBookmarkAdded` is
    // recreated (which happens after every save, when quota state changes).
    //
    // Unauthenticated handling: if a logged-out user shares a URL to BrainSync,
    // we stash the share intent in sessionStorage so it survives the OAuth
    // round-trip. The auth-check effect will redirect the user to the
    // landing page; once they sign in and return to /dashboard, this effect
    // re-runs (now authenticated) and pulls the stashed intent - opening
    // the Add Bookmark modal with the original URL pre-filled, exactly as
    // if they'd been signed in all along.
    const shareConsumedRef = useRef(false);
    // Pending-starter consumer. If the user landed on the desktop
    // landing (logged-out), clicked "Save to BrainSync" on one of the
    // starter video cards, and then signed up / signed in — we stash
    // the starter id in sessionStorage. Now that they're on the
    // Dashboard authenticated, clone that starter into their library
    // via the idempotent POST /bookmarks/starter/{id} endpoint and show
    // a toast so the click completes the flow they started before auth.
    const starterConsumedRef = useRef(false);
    useEffect(() => {
        if (starterConsumedRef.current) return;
        if (authLoading || !user) return;
        let payload = null;
        try {
            const raw = sessionStorage.getItem('bs_pending_starter');
            if (raw) {
                const parsed = JSON.parse(raw);
                // Expire stashed intents after 30 min - avoids
                // resurrecting a stale click days later.
                if (parsed && parsed.ts && Date.now() - parsed.ts < 30 * 60 * 1000 && parsed.starterId) {
                    payload = parsed;
                }
                sessionStorage.removeItem('bs_pending_starter');
            }
        } catch (_) { /* ignore parse errors */ }
        if (!payload) return;
        starterConsumedRef.current = true;
        (async () => {
            const { toast } = await import('sonner');
            try {
                const res = await fetch(`${API}/bookmarks/starter/${payload.starterId}`, {
                    method: 'POST',
                    credentials: 'include',
                });
                if (!res.ok) {
                    const body = await res.json().catch(() => ({}));
                    throw new Error(body.detail || `Save failed (${res.status})`);
                }
                const data = await res.json();
                toast.success('Saved to your BrainSync library.');
                // Refresh the list so the newly-cloned bookmark shows up
                // at the top without waiting for the next fetch tick.
                fetchBookmarksRef.current?.();
                // First-save auto-open: if this starter clone is the
                // user's very first save, deep-link them straight to
                // the bookmark detail page so the Ask BrainSync
                // coach-mark greets them there. Uses the same guard as
                // handleBookmarkAdded so we never double-navigate.
                try {
                    const bookmarkId = data?.bookmark_id || data?.id;
                    if (bookmarkId) {
                        // Server-derived first_save: backend enforces
                        // once-per-user via the unique index, so we can
                        // call on every starter-save without worrying
                        // about localStorage / totalCountRef state. See
                        // handleBookmarkAdded for the full rationale.
                        trackServer('first_save', { content_type: data?.content_type || 'video' });
                        if (totalCountRef.current === 0) {
                            setTimeout(() => {
                                navigate(`/bookmark/${bookmarkId}`);
                                track('first_save_auto_open', { content_type: data?.content_type || 'video', source: 'starter' });
                            }, 800);
                        }
                    }
                } catch (_) { /* nav must never break the save flow */ }
                return data;
            } catch (err) {
                toast.error(err.message || 'Could not save that video. Try again.');
            }
        })();
    }, [user, authLoading]);

    // Permissive URL extractor used by every share entry point in this
    // effect (eager stash, main handler, and pending-share consumer).
    // Handles both single-URL shares (YouTube, Reddit, X, TikTok — the
    // text field IS the URL) and mixed-text shares (Agoda, Booking,
    // Airbnb — the text field is "Check out <title>\n<url>"). Strips
    // trailing punctuation so URLs at sentence-end match cleanly.
    const extractFirstUrl = (raw) => {
        if (!raw || typeof raw !== 'string') return '';
        const m = raw.match(/https?:\/\/[^\s<>"']+/i);
        if (!m) return '';
        return m[0].replace(/[.,;:!?)}\]]+$/, '');
    };

    useEffect(() => {
        if (shareConsumedRef.current) return;
        const isShare = searchParams.get('share');
        const hasFile = searchParams.get('hasFile');
        const isNative = searchParams.get('native') === '1';

        // SW-miss recovery: the pre-React inline script stamps
        // `bs_share_miss` in sessionStorage when it lands on
        // /dashboard?share=true with NO extractable URL — that's the
        // fingerprint of Chrome's WebAPK POST arriving before the SW
        // was controlling (the multipart body was dropped by the
        // origin server). Show a friendly toast so the user knows
        // to re-share instead of sitting on a blank dashboard.
        try {
            const missTs = sessionStorage.getItem('bs_share_miss');
            if (missTs && user && !authLoading) {
                sessionStorage.removeItem('bs_share_miss');
                if (Date.now() - Number(missTs) < 60 * 1000) {
                    import('sonner').then(({ toast }) => {
                        toast.error("We couldn't read that link — please share it again from the app.", { duration: 6000 });
                    });
                }
            }
        } catch (_) { /* never block share flow on storage errors */ }

        // Share-intent gate: URL / text shares (Agoda, Booking, Trip,
        // NYT, YouTube, etc.). We used to eager-stash + navigate('/')
        // to landing WHILE auth was still resolving, in case the user
        // was logged out. That was correct in intent but broke the
        // common case: for signed-in users the bounce caused a
        // Dashboard→Landing→Dashboard remount cascade that visibly
        // reloaded the page and left the second Dashboard instance
        // racing pendingFromStorage against the still-transient
        // `user` object. The stash then got skipped by the
        // `if (user)` gate → no modal.
        //
        // Cleaner: just wait for auth to resolve. Auth-check is
        // ~200-500ms on a warm cookie, and up to 6s worst-case on
        // TWA cold-start. If we still don't have a user after that,
        // stash + bounce to landing. If we do, open the modal
        // directly - zero remounts, no visible reload.
        const urlShareRaw = (searchParams.get('url') || searchParams.get('text') || '').trim();
        const urlShareExtracted = urlShareRaw.startsWith('http')
            ? urlShareRaw
            : extractFirstUrl(urlShareRaw);
        const isUrlShare = !!urlShareExtracted && urlShareExtracted.startsWith('http') && hasFile !== '1';
        if (isUrlShare) {
            // Defensive stash: happens on EVERY isUrlShare regardless
            // of auth state. Belt-and-suspenders in case the SW's
            // clients.claim() forces a mid-page reload (Chrome does
            // this in some versions when a fresh SW takes control),
            // or any other layer strips the URL params between the
            // first and second effect fire. The pendingFromStorage
            // consumer below always reads this if searchParams is
            // empty on a subsequent mount.
            try {
                sessionStorage.setItem('bs_pending_share', JSON.stringify({
                    url: urlShareExtracted,
                    text: searchParams.get('text') || '',
                    title: searchParams.get('title') || '',
                    share: searchParams.get('share') || '',
                    ts: Date.now(),
                }));
            } catch (_) { /* sessionStorage full / disabled */ }

            if (authLoading) {
                // Auth still resolving - do NOT navigate. Wait for
                // the effect to re-fire when authLoading flips.
                return;
            }
            if (!user) {
                // Genuinely logged out: bounce to landing so the
                // user signs in. The stash above is what the post-
                // login Dashboard remount will consume.
                shareConsumedRef.current = true;
                navigate('/', { replace: true });
                return;
            }
            // Signed-in + valid share URL - open the modal directly.
            setAddModalInitialFile(null);
            setAddModalInitialMode('url');
            setAddModalInitialUrl(urlShareExtracted);
            setAddModalOpen(true);
            shareConsumedRef.current = true;
            // Clear the URL params AND the stash so a manual refresh
            // of /dashboard doesn't re-open the modal on the same
            // link.
            try { sessionStorage.removeItem('bs_pending_share'); } catch (_) { /* ignore */ }
            setSearchParams({}, { replace: true });
            return;
        }

        // Wait for auth state to settle for any other path (file shares,
        // already-authenticated URL shares).
        if (authLoading) return;

        // See if we have a pending share from a previous logged-out
        // attempt. Only consume it once we're authenticated.
        let pendingFromStorage = null;
        if (user) {
            try {
                const raw = sessionStorage.getItem('bs_pending_share');
                if (raw) {
                    const parsed = JSON.parse(raw);
                    // Expire stashed shares older than 30 minutes - avoids
                    // accidentally resurfacing a stale URL if the user
                    // signs in days later for an unrelated reason.
                    if (parsed && parsed.ts && Date.now() - parsed.ts < 30 * 60 * 1000) {
                        pendingFromStorage = parsed;
                    }
                    sessionStorage.removeItem('bs_pending_share');
                }
            } catch (_) { /* ignore parse errors */ }
        }

        if (isShare && hasFile === '1') {
            // Files can't be stashed across an OAuth round-trip (the Service
            // Worker cache / native bridge buffer is tied to this tab's
            // session). If the user is logged out, drop the file path and
            // bail - the user will be redirected to login by the auth-check
            // effect and the file will be lost. URL/text shares ARE
            // recoverable (handled below).
            if (!user) {
                setSearchParams({}, { replace: true });
                return;
            }
            // File was shared (image from Google Photos, etc.)
            (async () => {
                const { toast } = await import('sonner');
                try {
                    let file = null;

                    if (isNative && typeof window !== 'undefined' && window.BrainSyncShare) {
                        // Android WebView wrapper: pull the image from the native bridge.
                        // We support TWO bridge versions:
                        //   - v1.2.2+ chunked: getSharedImageLength / getSharedImageChunk / getSharedImageMime
                        //   - v1.2.1   single-call: getSharedImage() returns a full data: URL
                        const bridge = window.BrainSyncShare;
                        const filename = bridge.getSharedImageFilename?.() || 'shared-image.jpg';

                        if (typeof bridge.getSharedImageLength === 'function') {
                            // Chunked path
                            const total = bridge.getSharedImageLength() || 0;
                            if (!total) {
                                toast.error('Shared image was empty');
                                return;
                            }
                            toast.info(`Uploading shared image (${Math.round(total / 1024)} KB base64)…`);

                            const chunkSize = 256 * 1024;
                            let b64 = '';
                            for (let off = 0; off < total; off += chunkSize) {
                                b64 += bridge.getSharedImageChunk(off, chunkSize);
                            }
                            const mime = bridge.getSharedImageMime?.() || 'image/jpeg';
                            bridge.clearSharedImage?.();

                            const byteChars = atob(b64);
                            const byteNums = new Uint8Array(byteChars.length);
                            for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
                            file = new File([byteNums], filename, { type: mime });
                        } else if (typeof bridge.getSharedImage === 'function') {
                            // Legacy single-call path (v1.2.1 APK)
                            toast.info('Uploading shared image…');
                            const dataUrl = bridge.getSharedImage();
                            if (!dataUrl) {
                                toast.error('Shared image was empty (legacy bridge). Please update the BrainSync app to v1.2.3 for reliable sharing.');
                                return;
                            }
                            const resp = await fetch(dataUrl);
                            const blob = await resp.blob();
                            file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
                        } else {
                            toast.error('Native share bridge unavailable. Please update the BrainSync app to v1.2.3.');
                            return;
                        }
                    } else {
                        // PWA share target: read the file out of the service worker cache
                        const cache = await caches.open('share-target');
                        const fileResp = await cache.match('/_share_file');
                        if (fileResp) {
                            const blob = await fileResp.blob();
                            const filename = fileResp.headers.get('X-Filename') || 'shared-image.jpg';
                            file = new File([blob], filename, { type: blob.type });
                        }
                        // Clean up share cache
                        await cache.delete('/_share_file');
                        await cache.delete('/_share_data');
                    }

                    if (!file) {
                        toast.error('Could not read shared image');
                        return;
                    }

                    // Open the Add Bookmark modal in Manual mode with the
                    // shared image pre-loaded - gives the user a chance to
                    // pick categories, add tags, and write a note before saving.
                    setAddModalInitialFile(file);
                    setAddModalInitialMode('manual');
                    setAddModalInitialUrl('');
                    setAddModalOpen(true);
                    shareConsumedRef.current = true;
                } catch (err) {
                    console.error('Error handling shared file:', err);
                    toast.error(`Share failed: ${err?.message || err}`);
                }
                // Clear share params from react-router's state so the effect
                // won't re-trigger after the modal closes.
                setSearchParams({}, { replace: true });
            })();
            return;
        }

        // URL/text share - both the live URL params and the stashed
        // pending-share are valid sources. Prefer the live URL.
        //
        // Third-party apps split into two share-intent conventions:
        //   1. Send the URL as the whole text (YouTube, Reddit, X,
        //      Instagram Reels, TikTok, most social apps).
        //   2. Send "Check out <title>\n<url>" as text (Agoda,
        //      Booking.com, Airbnb, many e-commerce apps, iOS
        //      Safari's "Share" from the address bar when text
        //      selection is engaged).
        // We normalise both: extract the first http(s):// URL found
        // in the payload with a permissive regex. Falls back to the
        // raw text if no URL is present so a plain-text share still
        // opens the modal (user can then paste / discard).
        const shareTextRaw =
            searchParams.get('url')
            || searchParams.get('text')
            || searchParams.get('share')
            || pendingFromStorage?.url
            || pendingFromStorage?.text
            || pendingFromStorage?.share
            || '';

        // Same extractor used in the eager-stash block above - handles
        // both raw-URL shares and "Check out X\n<url>" mixed-text shares.
        const sharedUrl = shareTextRaw.startsWith('http')
            ? shareTextRaw
            : extractFirstUrl(shareTextRaw);

        if (sharedUrl && sharedUrl.startsWith('http')) {
            if (!user) {
                // Logged out: stash the share intent for after-login restore,
                // then clear the URL so the auth-check effect can redirect
                // to the landing page cleanly. The user will return here
                // post-login and this effect will re-run with `user` set
                // and pick up the stash automatically.
                try {
                    sessionStorage.setItem('bs_pending_share', JSON.stringify({
                        url: sharedUrl,
                        text: shareTextRaw,
                        title: searchParams.get('title') || '',
                        share: searchParams.get('share') || '',
                        ts: Date.now(),
                    }));
                } catch (_) { /* sessionStorage full / disabled */ }
                setSearchParams({}, { replace: true });
                return;
            }

            setAddModalInitialFile(null);
            setAddModalInitialMode('url');
            setAddModalInitialUrl(sharedUrl);
            setAddModalOpen(true);
            shareConsumedRef.current = true;
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams, user, authLoading, navigate]);

    const handleSaveCategory = async (e) => {
        e.preventDefault();
        if (!categoryName.trim()) return;

        setSubmitting(true);
        try {
            const url = editingCategory 
                ? `${API}/categories/${editingCategory.category_id}`
                : `${API}/categories`;
            
            const response = await fetch(url, {
                method: editingCategory ? 'PATCH' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    name: categoryName.trim(),
                    color: categoryColor,
                    icon: categoryIcon,
                    parent_id: categoryParentId || null,
                    description: categoryDescription.trim().slice(0, 140) || null,
                    manual_order_enabled: !!categoryManualOrder,
                    show_numbering: !!categoryShowNumbering,
                })
            });

            if (response.ok) {
                // Pull out the saved category - we need its id for
                // the follow-up share-visibility call below if the
                // privacy state changed during the edit.
                const saved = await response.json().catch(() => null);
                const targetId = (editingCategory && editingCategory.category_id) || saved?.category_id;
                const prevVis = editingCategory?.share_visibility || 'private';
                if (targetId && categoryShareVisibility !== prevVis) {
                    try {
                        await fetch(`${API}/categories/${targetId}/share`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({ visibility: categoryShareVisibility }),
                        });
                    } catch (_e) { /* surfaced by the categories refetch */ }
                }
                toast.success(editingCategory ? 'Collection updated!' : 'Collection created!');
                setCategoryModalOpen(false);
                resetCategoryForm();
                fetchCategories();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to save collection');
            }
        } catch (error) {
            toast.error('Failed to save collection');
            toast.error('Failed to save collection');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteCategory = async (categoryId) => {
        try {
            const response = await fetch(`${API}/categories/${categoryId}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (response.ok) {
                toast.success('Collection deleted');
                if (selectedCategory === categoryId) {
                    setSelectedCategory(null);
                }
                fetchCategories();
                fetchBookmarks();
            } else {
                toast.error('Failed to delete collection');
            }
        } catch (error) {
            toast.error('Failed to delete collection');
            toast.error('Failed to delete collection');
        } finally {
            setDeleteCategoryId(null);
        }
    };

    const resetCategoryForm = () => {
        setCategoryName('');
        setCategoryColor('#8B5CF6');
        setCategoryIcon('folder');
        setCategoryParentId('');
        setCategoryDescription('');
        setCategoryManualOrder(false);
        setCategoryShowNumbering(false);
        setCategoryShareVisibility('private');
        setEditingCategory(null);
        // Fresh collection → icon picker open by default so the user
        // is invited to pick a meaningful glyph. Edits start collapsed.
        setIconPickerOpen(true);
        setColorPickerOpen(false);
    };

    const openEditCategory = (category) => {
        setEditingCategory(category);
        setCategoryName(category.name);
        setCategoryColor(category.color);
        setCategoryIcon(category.icon || 'folder');
        setCategoryParentId(category.parent_id || '');
        setCategoryDescription(category.description || '');
        setCategoryManualOrder(!!category.manual_order_enabled);
        setCategoryShowNumbering(!!category.show_numbering);
        setCategoryShareVisibility(category.share_visibility || 'private');
        setIconPickerOpen(false);
        setColorPickerOpen(false);
        setCategoryModalOpen(true);
        setMobileMenuOpen(false);
    };

    const handleDeleteBookmark = async (bookmarkId) => {
        try {
            const response = await fetch(`${API}/bookmarks/${bookmarkId}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (response.ok) {
                toast.success('Bookmark deleted');
                fetchBookmarks();
                fetchCategories();
                fetchTags();
                fetchSubscriptionStatus();
            } else {
                toast.error('Failed to delete bookmark');
            }
        } catch (error) {
            toast.error('Failed to delete bookmark');
            toast.error('Failed to delete bookmark');
        }
    };

    const handleToggleFavorite = async (e, bookmarkId) => {
        e.stopPropagation();
        try {
            const response = await fetch(`${API}/bookmarks/${bookmarkId}/favorite`, {
                method: 'POST',
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setBookmarks(prev => prev.map(b => 
                    b.bookmark_id === bookmarkId ? { ...b, is_favorite: data.is_favorite } : b
                ));
            }
        } catch (error) {
            console.error('Error toggling favorite:', error);
        }
    };

    const handleReprocess = async (bookmarkId) => {
        try {
            const response = await fetch(`${API}/bookmarks/${bookmarkId}/reprocess`, {
                method: 'POST',
                credentials: 'include'
            });

            if (response.ok) {
                toast.success('Reprocessing started');
                fetchBookmarks();
            } else {
                toast.error('Failed to start reprocessing');
            }
        } catch (error) {
            toast.error('Failed to reprocess bookmark');
            toast.error('Failed to start reprocessing');
        }
    };

    const [shareModalItem, setShareModalItem] = useState(null); // { kind, item } when open
    // Separate state for the new Airbnb-style quick share - opened from
    // the bookmark thumbnail's share button. Keeping the heavier
    // ShareModal (which carries privacy + referral analytics) around
    // for surfaces that need its full feature set.
    // Native OS share takes care of the "send to a friend" path
    // directly from the bookmark card; no drawer state needed.
    const [referralsModalOpen, setReferralsModalOpen] = useState(false);
    const [usernameModalOpen, setUsernameModalOpen] = useState(false);
    const [shareDemoModalOpen, setShareDemoModalOpen] = useState(false);

    // Whether the user has at least one public bookmark or category - drives
    // the "Public" badge on the sidebar profile card. Cheap derived state
    // off the existing categories list + a memoized count of public
    // bookmarks isn't surfaced from the dashboard fetch yet, so we infer
    // from categories.share_visibility for v1. The card stays useful
    // either way (it always deep-links to the public profile).
    const hasPublicItems = useMemo(
        () => categories.some((c) => c.share_visibility === 'public'),
        [categories],
    );

    // One-tap "Share my BrainSync" - invoked from the user dropdown. Uses
    // the rich-preview URL (`/api/p/{username}`) so any social platform
    // parsing the link sees the per-user OG card; humans clicking the
    // link are bounced to the canonical `/{username}` via the server
    // shell's JS redirect. Tries native share sheet first (mobile),
    // falls back to clipboard. Logged-out users (defensive) get a
    // friendly nudge to sign in.
    //
    // The `?v={share_card_version}` suffix is critical. WhatsApp /
    // Facebook / Slack / LinkedIn cache OG previews per shared URL
    // for ~7 days regardless of Cache-Control headers. Bumping
    // CARD_DESIGN_VERSION on the backend (and bouncing it through
    // `/api/auth/me`) changes the shared URL, forcing every social
    // platform to re-scrape from scratch - the only reliable way to
    // make a redesigned OG card propagate without waiting for caches.
    const shareMyBrainSync = async () => {
        if (!user?.username) {
            toast.info('Sign in to create your public BrainSync profile.');
            return;
        }
        const versionSuffix = user?.share_card_version ? `?v=${user.share_card_version}` : '';
        const shareUrl = `${window.location.origin}/api/p/${user.username}${versionSuffix}`;
        const shareText = 'Take a look at my public bookmarks on BrainSync.';
        const shareTitle = `${user.name || user.username}'s BrainSync`;

        try {
            await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
            return;
        } catch (err) {
            if (err?.name === 'AbortError') return;
            // Android native bridge - calls into ShareBridge in
            // LauncherActivity.java, which fires Android's real
            // Intent.ACTION_SEND chooser. Only present in our APK.
            try {
                if (window.BrainSyncNative && typeof window.BrainSyncNative.share === 'function') {
                    window.BrainSyncNative.share(`${shareText}\n${shareUrl}`);
                    return;
                }
            } catch (_) { /* fall through to clipboard */ }
        }
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success('Share link copied - paste it anywhere for a rich preview');
        } catch (_) {
            toast.error('Could not copy link');
        }
    };

    const handleShareBookmark = async (e, bookmark) => {
        e.stopPropagation();
        // Share strategy (in order):
        //   1) navigator.share - works in Chrome browser, Safari, modern PWAs.
        //   2) window.BrainSyncNative.share - our APK's JavascriptInterface
        //      (LauncherActivity.java → ShareBridge) which fires the real
        //      Android Intent.ACTION_SEND chooser. This is the *only* way
        //      to get the native share sheet inside the Android WebView
        //      wrapper, where navigator.share is undefined.
        //   3) clipboard copy - universal last resort.
        // Share URL points to the backend SSR endpoint
        // (`/api/share/:id`) - not the React-only `/share/:id` route.
        // The backend serves bookmark-specific OG meta tags to crawlers
        // (WhatsApp, Twitter, Facebook…) and 302-redirects real browsers
        // to `/share/:id`. Pointing share URLs directly at `/share/:id`
        // breaks link previews because crawlers receive the generic
        // index.html with the app icon, not the bookmark thumbnail.
        const shareUrl = `${window.location.origin}/api/share/${bookmark.bookmark_id}`;
        const shareTitle = bookmark.title || 'Bookmark';
        try {
            await navigator.share({ title: shareTitle, text: shareTitle, url: shareUrl });
            return;
        } catch (err) {
            if (err?.name === 'AbortError') return;
            try {
                if (window.BrainSyncNative && typeof window.BrainSyncNative.share === 'function') {
                    window.BrainSyncNative.share(`${shareTitle}\n${shareUrl}`);
                    return;
                }
            } catch (_) { /* fall through */ }
            try {
                await navigator.clipboard.writeText(shareUrl);
                toast.success('Link copied - paste it anywhere');
            } catch (_) {
                toast.error('Could not copy link');
            }
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    // ─── Manual-order context ────────────────────────────────────
    // Which category's ordering is currently authoritative? Sub-
    // collection takes priority over its parent. We also gate manual
    // ordering on "no filters active" - once the user searches or
    // tags-filters, ranks would look broken (gaps in the sequence), so
    // we fall back to date-sort until they clear the filter.
    const manualOrderCategoryId = selectedSubcategory || selectedCategory || null;
    const manualOrderCategory = useMemo(
        () => (manualOrderCategoryId ? categories.find(c => c.category_id === manualOrderCategoryId) : null),
        [categories, manualOrderCategoryId],
    );
    const filtersActive = !!(searchQuery || selectedTag || selectedType || showFavorites);
    const manualOrderActive = !!(manualOrderCategory?.manual_order_enabled && !filtersActive);
    const showNumbering = !!(manualOrderActive && manualOrderCategory?.show_numbering);

    const sortedBookmarks = [...bookmarks]
        // Client-side subcategory filtering
        .filter(bm => {
            if (selectedSubcategory) {
                const bmCats = bm.category_ids || (bm.category_id ? [bm.category_id] : []);
                return bmCats.includes(selectedSubcategory);
            }
            return true;
        })
        .sort((a, b) => {
        // When the curator has set a manual order, the backend already
        // returns items in the right sequence - don't re-sort them.
        if (manualOrderActive) return 0;
        switch (sortBy) {
            case 'created_desc': return new Date(b.created_at) - new Date(a.created_at);
            case 'created_asc': return new Date(a.created_at) - new Date(b.created_at);
            case 'edited_desc': return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
            case 'edited_asc': return new Date(a.updated_at || a.created_at) - new Date(b.updated_at || b.created_at);
            case 'title_asc': return (a.title || '').localeCompare(b.title || '');
            case 'title_desc': return (b.title || '').localeCompare(a.title || '');
            default: return 0;
        }
    });

    // ─── Drag-and-drop reordering ────────────────────────────────
    // dnd-kit sensors: PointerSensor for mouse, TouchSensor with a 200ms
    // long-press activation so taps in the grid don't accidentally
    // start a drag, KeyboardSensor for accessibility.
    const dndSensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleReorderDragEnd = useCallback(async (event) => {
        const { active, over } = event || {};
        if (!over || active.id === over.id) return;
        if (!manualOrderCategoryId) return;
        const ids = sortedBookmarks.map(b => b.bookmark_id);
        const oldIndex = ids.indexOf(active.id);
        const newIndex = ids.indexOf(over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        const reorderedIds = arrayMove(ids, oldIndex, newIndex);
        // Optimistically re-order the local `bookmarks` state so the UI
        // snaps into place without waiting for the server. We map by
        // bookmark_id, preserving every other bookmark's relative order
        // (only the slice for this category gets re-arranged).
        setBookmarks(prev => {
            const map = new Map(prev.map(b => [b.bookmark_id, b]));
            const inSet = new Set(reorderedIds);
            const reorderedItems = reorderedIds.map(id => map.get(id)).filter(Boolean);
            const others = prev.filter(b => !inSet.has(b.bookmark_id));
            // The visible category's items go first (so the grid order
            // matches the user's drag), with off-scope items after.
            return [...reorderedItems, ...others];
        });
        try {
            const resp = await fetch(`${API}/categories/${manualOrderCategoryId}/order`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ bookmark_ids: reorderedIds }),
            });
            if (!resp.ok) throw new Error('reorder failed');
        } catch (_err) {
            toast.error('Could not save the new order. Refresh to retry.');
        }
    }, [manualOrderCategoryId, sortedBookmarks]);

    const selectCategory = (categoryId) => {
        setSelectedCategory(categoryId);
        // Clear any active subcategory selection - otherwise picking "All
        // Bookmarks" (categoryId=null) or jumping to a different parent
        // category leaves the previous subcategory filter applied, so the
        // bookmark list stays scoped to the old sub. See bug repro: open a
        // subcategory → tap All Bookmarks → list still filtered.
        setSelectedSubcategory(null);
        if (categoryId) setShowFavorites(false);
        setMobileMenuOpen(false);

        // Mark shared category as viewed to clear "new" badge
        if (categoryId) {
            const cat = categories.find(c => c.category_id === categoryId);
            if (cat?.is_shared && cat?.new_since_last_view > 0) {
                fetch(`${API}/categories/${categoryId}/viewed`, { method: 'POST', credentials: 'include' })
                    .then(() => {
                        setCategories(prev => prev.map(c =>
                            c.category_id === categoryId ? { ...c, new_since_last_view: 0 } : c
                        ));
                    })
                    .catch((err) => console.error('Failed to mark category viewed:', err));
            }
        }
    };

    const getCategoryById = (categoryId) => {
        return categories.find(c => c.category_id === categoryId);
    };

    const getBookmarkCategories = (bookmark) => {
        const catIds = bookmark.category_ids || (bookmark.category_id ? [bookmark.category_id] : []);
        return catIds.map(id => getCategoryById(id)).filter(Boolean);
    };

    // Sidebar Content Component (shared between desktop and mobile)

    if ((authLoading && !hydrated.current) || loading) {
        // Keep the HTML splash visible (don't dismiss it) so we don't
        // double-flash a React loader on top of the splash that's
        // already on-screen. The splash auto-dismisses on a safety
        // timer if it ever gets stuck.
        return null;
    }

    // Dashboard is ready to paint - dismiss the splash. Effect runs
    // synchronously below; this inline call covers the first render.
    if (typeof window !== 'undefined') {
        window.__brainsyncDismissSplash?.();
    }

    return (
        <div className="min-h-screen bg-background overflow-x-hidden" data-testid="dashboard">
            <Toaster position="top-center" richColors />
            <ReminderAlerts dueReminders={dueReminders} onDismiss={dismissReminder} onSnooze={snoozeReminder} />
            
            <DashboardHeader
                navigate={navigate}
                brandScrollProgress={brandScrollProgress}
                headerGreeting={headerGreeting}
                mobileMenuOpen={mobileMenuOpen}
                setMobileMenuOpen={setMobileMenuOpen}
                sidebarProps={{
                    user,
                    hasPublicItems,
                    selectedCategory,
                    selectedSubcategory,
                    showFavorites,
                    ownedParentCategories,
                    sharedWithMeCategories,
                    categories,
                    totalCount,
                    selectCategory,
                    setSelectedCategory,
                    setSelectedSubcategory,
                    setShowFavorites,
                    setMobileMenuOpen,
                    resetCategoryForm,
                    setCategoryModalOpen,
                    openEditCategory,
                    setShareCategoryTarget,
                    setShareCategoryOpen,
                    setCategoryParentId,
                    setDeleteCategoryId,
                }}
                setSelectedCategory={setSelectedCategory}
                setSelectedSubcategory={setSelectedSubcategory}
                setShowFavorites={setShowFavorites}
                totalCount={totalCount}
                favoritesCount={favoritesCount}
                unreadCount={unreadCount}
                notifOpen={notifOpen}
                notifications={notifications}
                handleOpenNotifications={handleOpenNotifications}
                closeNotifications={closeNotifications}
                user={user}
                subscriptionInfo={subscriptionInfo}
                setUpgradeModalOpen={setUpgradeModalOpen}
                shareMyBrainSync={shareMyBrainSync}
                setShareDemoModalOpen={setShareDemoModalOpen}
                setUsernameModalOpen={setUsernameModalOpen}
                setReferralsModalOpen={setReferralsModalOpen}
                biometric={biometric}
                isDesktopChrome={isDesktopChrome}
                handleLogout={handleLogout}
            />

            {/* App Install Banners */}
            <AndroidAppBanner />
            <IOSInstallBanner />

            {/* Persistent "Psst" feedback pill - lets first users
                reach the founder without leaving the app. Auto-hidden
                on unauthed / landing routes (via the internal
                `!user` guard inside the component). */}
            <FeedbackPill />

            {/* Chrome Extension Banner - Desktop Chrome only.
                Suppressed when the user is known to already have
                the extension installed. Two independent signals:
                 - `user.extension_seen`: backend flag set the first
                   time the extension calls a write endpoint with
                   `X-BrainSync-Client: extension/...` (v1.1.2+).
                   Persists across devices for that user.
                 - `window.__brainsync_extension.installed`: DOM-side
                   marker set by the extension's content script on
                   every brainsync.pro page load. Real-time detection
                   even before the first extension-driven save. */}
            {!extensionBannerDismissed && isDesktopChrome && !user?.extension_seen && !extensionDetectedInPage && (() => {
                const storeUrl = process.env.REACT_APP_CHROME_WEB_STORE_URL || '';
                const useStore = !!storeUrl;
                return (
                <div data-testid="extension-banner">
                    <div className="max-w-7xl mx-auto px-4 pt-3">
                        <div className="flex items-center justify-between gap-4 px-4 py-2.5 rounded-xl border border-primary/15 bg-primary/[0.03]">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                    <Globe className="w-4 h-4 text-primary" />
                                </div>
                                <p className="text-sm text-foreground">
                                    <span className="font-semibold">Save pages faster.</span>
                                    <span className="text-muted-foreground ml-1">Get the BrainSync Chrome extension - one click to save any page.</span>
                                </p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <a
                                    href={useStore ? storeUrl : '/brainsync-extension.zip'}
                                    {...(useStore ? { target: '_blank', rel: 'noopener noreferrer' } : { download: true })}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-semibold hover:opacity-90 transition-opacity"
                                    data-testid="extension-download-banner"
                                >
                                    <Download className="w-3.5 h-3.5" />
                                    {useStore ? 'Add to Chrome' : 'Download'}
                                </a>
                                <button
                                    onClick={() => { setExtensionBannerDismissed(true); localStorage.setItem('bs_ext_banner_dismissed', '1'); }}
                                    className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-secondary transition-colors"
                                    data-testid="extension-banner-dismiss"
                                >
                                    <X className="w-4 h-4 text-muted-foreground" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                );
            })()}

            {/* Shadow Account Banner */}
            {user?.is_shadow && (
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-100" data-testid="shadow-account-banner">
                    <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                            <Sparkles className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                            <p className="text-sm text-emerald-800 truncate">
                                <span className="font-medium">You're exploring BrainSync!</span>
                                <span className="hidden sm:inline"> Sign in to save your bookmarks permanently.</span>
                            </p>
                        </div>
                        <Button 
                            onClick={() => login()}
                            size="sm" 
                            className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg px-4 text-xs flex-shrink-0"
                            data-testid="shadow-claim-btn"
                        >
                            Sign in with Google
                        </Button>
                    </div>
                </div>
            )}

            {/* Premium AI quota progress bar.
                Free (Basic): always visible - the lifetime 20 saves matter.
                Paid (Plus): only visible at 80%+ (40/50) so we don't pester right after upgrade.
                Pro (200/mo): only at 80%+ (160/200), same reason as Plus. */}
            {subscriptionInfo.premium_limit && subscriptionInfo.premium_limit !== -1 && bookmarks.length > 0 && (() => {
                const used = subscriptionInfo.premium_used ?? 0;
                const limit = subscriptionInfo.premium_limit;
                const tier = subscriptionInfo.tier || 'free';
                // Fix C1 (Aug 2026): keep the free-tier quota banner
                // out of the way for the first few saves. Everyone on
                // Basic sees nothing until their fifth Premium AI
                // save has landed — new users aren't bombarded with a
                // quota bar while they're still figuring the product
                // out.
                if (tier === 'free' && used < 5) return null;
                // Hide for paid subscribers until they've used 80% of their monthly quota
                if ((tier === 'pro_200' || tier === 'pro_unlimited') && used < Math.round(limit * 0.8)) return null;
                const remaining = Math.max(0, limit - used);
                const pct = Math.min(100, Math.round((used / limit) * 100));
                const isNear = pct >= 80 && pct < 100;
                const isFull = used >= limit;
                const isLow = pct < 50;
                const tierLabel = tier === 'free' ? 'BrainSync Basic' : (tier === 'pro_200' ? 'BrainSync Plus' : 'BrainSync Pro');
                // Format the renewal date from `current_period_end` (ISO)
                // into a human-friendly locale-aware date. Guards against
                // a malformed/missing value so the banner still renders
                // for edge-case subscription docs.
                let renewalDate = '';
                if ((tier === 'pro_200' || tier === 'pro_unlimited') && subscriptionInfo.current_period_end) {
                    try {
                        const d = new Date(subscriptionInfo.current_period_end);
                        if (!isNaN(d.getTime())) {
                            renewalDate = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
                        }
                    } catch (_) { /* ignore */ }
                }
                return (
                    <div className="max-w-7xl mx-auto px-4 pt-3" data-testid="usage-progress-bar">
                        <div
                            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border cursor-pointer transition-colors ${isFull ? 'border-red-200 bg-red-50/60 hover:bg-red-50' : isNear ? 'border-amber-200 bg-amber-50/60 hover:bg-amber-50' : 'border-border bg-secondary/30 hover:bg-secondary/50'}`}
                            onClick={() => setAiUsageStatsOpen(true)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setAiUsageStatsOpen(true); }}
                            data-testid="usage-progress-bar-btn"
                        >
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className={`text-xs font-medium ${isFull ? 'text-red-700' : isNear ? 'text-amber-700' : 'text-muted-foreground'}`}>
                                        {isFull
                                            ? `You've used all ${limit} Premium AI saves on ${tierLabel} this month. New saves are Basic (no AI).${renewalDate ? ` Your quota renews on ${renewalDate}.` : ''}`
                                            : `${remaining} of ${limit} Premium AI saves left on ${tierLabel}${renewalDate ? ` · renews ${renewalDate}` : ''}${isLow && tier === 'free' ? ' - upgrade for more' : ''}`}
                                    </span>
                                    <span className={`text-xs font-semibold ${isFull ? 'text-red-600' : isNear ? 'text-amber-600' : 'text-muted-foreground'}`}>
                                        {used}/{limit}
                                    </span>
                                </div>
                                <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                                    <div
                                        className={`h-full rounded-full transition-all duration-500 ${isFull ? 'bg-red-500' : isNear ? 'bg-amber-500' : 'bg-primary/60'}`}
                                        style={{ width: `${Math.max(pct, 2)}%` }}
                                    />
                                </div>
                            </div>
                            <button
                                type="button"
                                    onClick={(e) => { e.stopPropagation(); setUpgradeModalOpen(true); }}
                                    className={`text-xs font-semibold whitespace-nowrap rounded-full px-3 py-1 transition-colors ${isFull ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-primary text-white hover:bg-primary/90'}`}
                                    data-testid="usage-progress-bar-upgrade-btn"
                                >
                                    {isFull ? 'Upgrade now' : 'Upgrade'}
                                </button>
                            </div>
                        </div>
                    );
                })()}

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 py-4 lg:py-6">
                <div className="flex gap-6">
                    {/* Desktop Sidebar */}
                    <aside className="hidden lg:block w-64 flex-shrink-0">
                        <div className="sticky top-20 bg-white rounded-2xl border border-border p-4 shadow-sm">
                            <CategorySidebar
                                user={user}
                                hasPublicItems={hasPublicItems}
                                selectedCategory={selectedCategory}
                                selectedSubcategory={selectedSubcategory}
                                showFavorites={showFavorites}
                                ownedParentCategories={ownedParentCategories}
                                sharedWithMeCategories={sharedWithMeCategories}
                                categories={categories}
                                totalCount={totalCount}
                                selectCategory={selectCategory}
                                setSelectedCategory={setSelectedCategory}
                                setSelectedSubcategory={setSelectedSubcategory}
                                setShowFavorites={setShowFavorites}
                                setMobileMenuOpen={setMobileMenuOpen}
                                resetCategoryForm={resetCategoryForm}
                                setCategoryModalOpen={setCategoryModalOpen}
                                openEditCategory={openEditCategory}
                                setShareCategoryTarget={setShareCategoryTarget}
                                setShareCategoryOpen={setShareCategoryOpen}
                                setCategoryParentId={setCategoryParentId}
                                setDeleteCategoryId={setDeleteCategoryId}
                            />
                        </div>
                    </aside>

                    {/* Main Content Area */}
                    <div className="flex-1 min-w-0">
                        {/* Search and Add - hidden on first-run empty
                            state. With zero bookmarks there's nothing
                            to search or filter, and FirstRunWelcome
                            provides its own inline Save URL input, so
                            this row would only add visual noise. */}
                        {(bookmarks.length > 0 || selectedCategory || selectedSubcategory || showFavorites || debouncedSearch.trim()) && (
                        <>
                        <div className="flex gap-2 sm:gap-3 mb-2 sm:mb-3">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search or ask"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onFocus={() => setSearchFocused(true)}
                                    onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                                    onKeyDown={(e) => {
                                        // Enter hands off to the /search
                                        // page for a prose+sources answer.
                                        // The dashboard search bar stays
                                        // regex-only so users always get
                                        // instant keyword results while
                                        // typing.
                                        if (e.key === 'Enter' && searchQuery.trim()) {
                                            e.preventDefault();
                                            e.currentTarget.blur();
                                            handleAiSearch(searchQuery);
                                        }
                                    }}
                                    className="pl-9 pr-24 py-5 rounded-xl bg-white border-border text-sm"
                                    data-testid="search-input"
                                />
                                {/* Right-side controls: Ask-AI shortcut
                                    is always visible so users have a
                                    one-tap route to the RAG page even
                                    when they're not sure how to phrase
                                    a query. Clear-X only appears when
                                    there's text to clear. */}
                                <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                                    {searchQuery && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                // Clear the input AND the
                                                // debounced value in the same
                                                // tick, and mark a fresh
                                                // fetch as loading. Without
                                                // this, the 250ms debounce
                                                // gap between input-clear
                                                // and the refetch lets the
                                                // guard `bookmarks.length===0
                                                // && !debouncedSearch` win
                                                // briefly, which flashes the
                                                // FirstRunWelcome onboarding
                                                // panel between two valid
                                                // dashboard states.
                                                setSearchQuery('');
                                                setDebouncedSearch('');
                                                setLoading(true);
                                            }}
                                            className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-secondary transition-colors"
                                            title="Clear search"
                                            data-testid="clear-search-btn"
                                        >
                                            <X className="w-4 h-4 text-muted-foreground" />
                                        </button>
                                    )}
                                    {voice.isSupported && (
                                        <button
                                            type="button"
                                            onClick={() => voice.listening ? voice.stop() : voice.start()}
                                            className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${voice.listening ? 'bg-red-100 hover:bg-red-200 animate-pulse' : 'hover:bg-secondary'}`}
                                            title={voice.listening ? 'Stop listening' : 'Voice search'}
                                            data-testid="voice-search-btn"
                                            aria-label={voice.listening ? 'Stop listening' : 'Voice search'}
                                        >
                                            <Mic className={`w-4 h-4 ${voice.listening ? 'text-red-600' : 'text-muted-foreground'}`} />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => handleAiSearch(searchQuery)}
                                        className="w-7 h-7 rounded-lg flex items-center justify-center bg-accent/50 hover:bg-accent transition-colors"
                                        title="Ask BrainSync"
                                        data-testid="ask-ai-shortcut-btn"
                                    >
                                        <Brain className="w-4 h-4 text-accent-foreground" />
                                    </button>
                                </div>
                            </div>

                            {/* Filter Buttons - Desktop */}
                            <FiltersBar
                                tags={tags}
                                selectedTag={selectedTag}
                                setSelectedTag={setSelectedTag}
                                selectedType={selectedType}
                                setSelectedType={setSelectedType}
                            />

                            <QuickCaptureButtons
                                onBookmarkAdded={handleBookmarkAdded}
                                onLimitReached={handleLimitReached}
                            />

                            <Button 
                                onClick={() => setAddModalOpen(true)}
                                className="rounded-xl h-10 px-3 sm:px-4 bg-primary hover:bg-primary/90 shadow-md shadow-primary/20"
                                data-testid="add-bookmark-btn"
                            >
                                <Plus className="w-4 h-4 sm:mr-1.5" />
                                <span className="hidden sm:inline text-sm">Add</span>
                            </Button>
                        </div>

                        {/* "Ask BrainSync" escalation chip. Appears
                            when the fast regex search returns ≤2 hits
                            OR when the query looks like a natural-
                            language question. Clicking navigates to
                            /search?q=... where the query is auto-
                            submitted against the full-library RAG
                            pipeline (same code path as the standalone
                            Ask-AI page). This replaces the earlier
                            inline summary/pill approach. */}
                        {debouncedSearch.trim() && (bookmarks.length <= 2 || looksLikeNaturalLanguage(debouncedSearch)) && (
                            <div className="mb-4 sm:mb-6">
                                <button
                                    type="button"
                                    onClick={() => handleAiSearch(debouncedSearch)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-accent/60 hover:bg-accent/80 text-accent-foreground border border-accent-foreground/20 transition-colors"
                                    data-testid="ai-search-escalate-chip"
                                >
                                    <Brain className="w-3 h-3" />
                                    <span>Ask BrainSync about &ldquo;{debouncedSearch.trim()}&rdquo;</span>
                                </button>
                            </div>
                        )}
                        {!(debouncedSearch.trim() && (bookmarks.length <= 2 || looksLikeNaturalLanguage(debouncedSearch))) && <div className="mb-4 sm:mb-6" />}

                        {/* Recent AI queries: a one-tap chip row for
                            re-running past questions without retyping.
                            Only shown when the input is focused AND no
                            active search / filter is in play — keeps
                            the dashboard clean when the user isn't
                            actively searching. Includes a small "Clear"
                            control so long-lived accounts can prune
                            stale queries without opening settings. */}
                        {searchFocused && !debouncedSearch.trim() && !selectedCategory && !selectedSubcategory && !showFavorites && aiQueryHistory.length > 0 && (
                            <div className="mb-4 sm:mb-6 flex flex-wrap items-center gap-2" data-testid="recent-ai-queries">
                                <span className="text-xs text-muted-foreground mr-1">Recent</span>
                                {aiQueryHistory.map((q) => (
                                    <button
                                        key={q}
                                        type="button"
                                        // onMouseDown fires BEFORE the input's blur,
                                        // so this click reliably lands even though
                                        // blur hides the row 200ms later.
                                        onMouseDown={(e) => { e.preventDefault(); handleAiSearch(q); }}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-secondary hover:bg-secondary/70 text-foreground/80 border border-border transition-colors truncate max-w-[220px]"
                                        data-testid="recent-ai-query-chip"
                                        title={q}
                                    >
                                        <Brain className="w-3 h-3 text-accent-foreground flex-shrink-0" />
                                        <span className="truncate">{q}</span>
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        if (user?.user_id) {
                                            clearAiQueries(user.user_id);
                                            setAiQueryHistory([]);
                                        }
                                    }}
                                    className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 ml-1"
                                    data-testid="clear-recent-ai-queries"
                                >
                                    Clear
                                </button>
                            </div>
                        )}
                        </>
                        )}

                        {/* Active scope title + Sort
                            Replaces the previous horizontal pill bar.
                            Users navigate categories via the sidebar
                            instead; this row simply confirms WHICH
                            scope is currently being viewed and keeps
                            the Sort action where users expect it.
                            Hidden on the first-run empty state - no
                            list yet means no breadcrumb is useful. */}
                        {(bookmarks.length > 0 || selectedCategory || selectedSubcategory || showFavorites) && (
                        <div className="mb-3 min-w-0" data-testid="categories-bar">
                            <div className="flex items-center gap-2 min-w-0">
                                <div className="flex-1 min-w-0 flex items-baseline gap-1.5 flex-wrap">
                                    {(() => {
                                        // Resolve display strings for the
                                        // active scope. Subcategory paths
                                        // render as "Parent › Subcategory".
                                        const activeCat = selectedCategory
                                            ? categories.find(c => c.category_id === selectedCategory)
                                            : null;
                                        const activeSub = selectedSubcategory
                                            ? categories.find(c => c.category_id === selectedSubcategory)
                                            : null;
                                        let title = 'All Bookmarks';
                                        if (showFavorites) {
                                            title = 'Favorites';
                                        } else if (activeSub) {
                                            title = activeSub.name;
                                        } else if (activeCat) {
                                            title = activeCat.name;
                                        }
                                        return (
                                            <>
                                                {showFavorites && <Heart className="w-4 h-4 text-red-500 fill-red-500 shrink-0 self-center" />}
                                                <h2
                                                    className="font-heading text-lg sm:text-xl font-semibold text-foreground truncate"
                                                    data-testid="scope-title"
                                                >
                                                    {title}
                                                </h2>
                                                <BackToAdminButton />
                                                {/* Privacy status pill -
                                                    the dashboard echo of
                                                    the sidebar icon, so
                                                    the curator always
                                                    sees the current
                                                    sharing posture of
                                                    the collection they
                                                    are viewing. */}
                                                {(() => {
                                                    const vis = (activeSub || activeCat)?.share_visibility;
                                                    if (vis === 'public') {
                                                        return (
                                                            <span
                                                                className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border border-emerald-500/40 text-emerald-700 bg-emerald-50 self-center"
                                                                title="Public - discoverable on your profile"
                                                                data-testid="scope-privacy-public"
                                                            >
                                                                <Globe className="w-3 h-3" />
                                                                Public
                                                            </span>
                                                        );
                                                    }
                                                    if (vis === 'unlisted') {
                                                        return (
                                                            <span
                                                                className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border border-sky-500/40 text-sky-700 bg-sky-50 self-center"
                                                                title="Unlisted - anyone with the link"
                                                                data-testid="scope-privacy-unlisted"
                                                            >
                                                                <Link2 className="w-3 h-3" />
                                                                Unlisted
                                                            </span>
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </>
                                        );
                                    })()}
                                </div>

                            </div>
                            {/* Curator-supplied tagline under the scope
                                title. Empty for the synthetic "All
                                Bookmarks" / "Favorites" scopes and for
                                collections without a description. Kept
                                outside the title flex row so a long
                                description can wrap to multiple lines
                                without pushing the Share/Sort buttons
                                off-screen on narrow viewports. */}
                            {(() => {
                                const activeCat = selectedSubcategory
                                    ? categories.find(c => c.category_id === selectedSubcategory)
                                    : (selectedCategory ? categories.find(c => c.category_id === selectedCategory) : null);
                                const desc = activeCat?.description;
                                if (!desc) return null;
                                return (
                                    <p className="text-sm text-muted-foreground mt-1 max-w-prose" data-testid="scope-description">{desc}</p>
                                );
                            })()}
                            {/* Action buttons row - moved BELOW the
                                title + description so a long collection
                                name no longer gets truncated by Share /
                                Edit / Sort competing for horizontal
                                space. On a narrow mobile viewport the
                                title now gets the full row width. */}
                            <div className="flex items-center gap-1 mt-2 -ml-1.5">
                                {/* Share + Edit buttons (preserved, rendered after the title column) */}
                                {(() => {
                                    const target = selectedSubcategory
                                        ? categories.find(c => c.category_id === selectedSubcategory)
                                        : (selectedCategory ? categories.find(c => c.category_id === selectedCategory) : null);
                                    if (!target) return null;
                                    // Edit is shown to the OWNER and to
                                    // shared editors. Categories the
                                    // user owns have `my_role=null`
                                    // (owners aren't members-with-role
                                    // - they're the owner). Anyone with
                                    // `my_role === 'viewer'` is read-only
                                    // and must not see Edit.
                                    const canEdit = !target.my_role || target.my_role === 'owner' || target.my_role === 'editor';
                                    return (
                                        <>
                                        <button
                                            type="button"
                                            onClick={() => { setShareCategoryTarget(target); setShareCategoryOpen(true); }}
                                            className="shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-mono text-muted-foreground hover:bg-secondary transition-colors"
                                            title="Share this collection"
                                            data-testid="scope-share-btn"
                                    >
                                        <Share2 className="w-3.5 h-3.5" />
                                        Share
                                    </button>
                                    {canEdit && (
                                        <button
                                            type="button"
                                            onClick={() => openEditCategory(target)}
                                            className="shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-mono text-muted-foreground hover:bg-secondary transition-colors"
                                            title="Edit this collection"
                                            data-testid="scope-edit-btn"
                                        >
                                            <Edit2 className="w-3.5 h-3.5" />
                                            Edit
                                        </button>
                                    )}
                                    </>
                                );
                            })()}

                            {/* Sort button - preserved from the previous design */}
                            <div className="flex-shrink-0">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-mono text-muted-foreground hover:bg-secondary transition-colors" data-testid="sort-btn">
                                            <ArrowUpDown className="w-3 h-3" />
                                            Sort
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-44">
                                        <DropdownMenuItem onClick={() => setSortBy('created_desc')} className={sortBy === 'created_desc' ? 'font-semibold' : ''}>
                                            Newest first
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => setSortBy('created_asc')} className={sortBy === 'created_asc' ? 'font-semibold' : ''}>
                                            Oldest first
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => setSortBy('edited_desc')} className={sortBy === 'edited_desc' ? 'font-semibold' : ''}>
                                            Last edited
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => setSortBy('title_asc')} className={sortBy === 'title_asc' ? 'font-semibold' : ''}>
                                            Title A-Z
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => setSortBy('title_desc')} className={sortBy === 'title_desc' ? 'font-semibold' : ''}>
                                            Title Z-A
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                                </div>
                            </div>
                        </div>
                        )}

                        {/* Subcategories row removed - sidebar handles
                            sub-collection nav now, and the title row
                            above already shows "Parent › Subcategory". */}

                        {/* Active Filters */}
                        {(selectedTag || selectedType) && (
                            <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
                                {selectedTag && (
                                    <Button 
                                        variant="secondary" 
                                        size="sm" 
                                        className="rounded-full text-xs h-7 px-3 flex-shrink-0"
                                        onClick={() => setSelectedTag(null)}
                                    >
                                        {selectedTag}
                                        <X className="w-3 h-3 ml-1" />
                                    </Button>
                                )}
                                {selectedType && (
                                    <Button 
                                        variant="secondary" 
                                        size="sm" 
                                        className="rounded-full text-xs h-7 px-3 flex-shrink-0"
                                        onClick={() => setSelectedType(null)}
                                    >
                                        {selectedType}
                                        <X className="w-3 h-3 ml-1" />
                                    </Button>
                                )}
                            </div>
                        )}

                        {/* Bookmarks Grid */}
                        {(() => {
                            // Fix C2 (Aug 2026): claim-path users get a
                            // warmer welcome state for their first
                            // three bookmarks. Only kicks in on the
                            // default library view — filters, search,
                            // and category scopes fall through to the
                            // normal grid so the user's intent is
                            // respected.
                            const claimPathWelcomeActive = FLAGS.CLAIM_PATH_ONBOARDING
                                && !!user?.onboarded_via_anon_save
                                && totalCount < 3
                                && !debouncedSearch.trim()
                                && !selectedTag
                                && !selectedType
                                && !selectedCategory
                                && !showFavorites;
                            if (claimPathWelcomeActive) {
                                const claimBookmarks = sortedBookmarks.slice(0, 2);
                                const bookmarksSlot = claimBookmarks.length ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 max-w-2xl">
                                        {claimBookmarks.map((bookmark) => {
                                            const isProcessing = bookmark.status === 'pending' || bookmark.status === 'processing';
                                            return (
                                                <BookmarkCard
                                                    key={bookmark.bookmark_id}
                                                    bookmark={bookmark}
                                                    isProcessing={isProcessing}
                                                    hasReminder={!!bookmarkReminders[bookmark.bookmark_id]}
                                                    categories={categories}
                                                    onNavigate={navigateToBookmark}
                                                    onReprocess={handleReprocess}
                                                    onToggleFavorite={handleToggleFavorite}
                                                    onShare={handleShareBookmark}
                                                    onDelete={handleDeleteBookmark}
                                                    onUpgradeRequest={() => setUpgradeModalOpen(true)}
                                                />
                                            );
                                        })}
                                    </div>
                                ) : null;
                                return (
                                    <ClaimPathWelcome
                                        userName={user?.name}
                                        bookmarksSlot={bookmarksSlot}
                                        syncing={claimBookmarks.length === 0}
                                        onQuickAdd={(url) => {
                                            setAddModalInitialUrl(url);
                                            setAddModalInitialMode('url');
                                            setAddModalOpen(true);
                                        }}
                                        onOpenAdd={() => setAddModalOpen(true)}
                                        onStarterSaved={() => { fetchBookmarks(); }}
                                    />
                                );
                            }
                            return null;
                        })()}
                        {(() => {
                            const claimPathWelcomeActive = FLAGS.CLAIM_PATH_ONBOARDING
                                && !!user?.onboarded_via_anon_save
                                && totalCount < 3
                                && !debouncedSearch.trim()
                                && !selectedTag
                                && !selectedType
                                && !selectedCategory
                                && !showFavorites;
                            if (claimPathWelcomeActive) return null;
                            return (
                                bookmarks.length === 0 ? (
                            debouncedSearch.trim() ? (
                                // Empty-search-result state. Explicitly
                                // separate from FirstRunWelcome so the
                                // user isn't dumped back into onboarding
                                // when a keyword lookup happens to
                                // return zero hits. The primary CTA is
                                // "Ask BrainSync" - RAG can find
                                // matches that regex can't (typos, ideas
                                // buried in transcripts, cross-language).
                                // Icon and copy are intentionally
                                // understated: the previous "big
                                // magnifying glass" was reading as a
                                // "search failed" error, not an
                                // invitation to try the smarter path.
                                <motion.div
                                    className="text-center py-14"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    data-testid="empty-search-state"
                                >
                                    <p className="text-sm text-muted-foreground mb-1">
                                        No quick matches for <span className="font-medium text-foreground">&ldquo;{debouncedSearch.trim()}&rdquo;</span>
                                    </p>
                                    <p className="text-sm text-muted-foreground mb-6">
                                        BrainSync AI can find matches by meaning &mdash; not just words.
                                    </p>
                                    <Button
                                        onClick={() => handleAiSearch(debouncedSearch)}
                                        className="rounded-full px-6 bg-primary hover:bg-primary/90 shadow-md shadow-primary/20"
                                        data-testid="empty-search-ai-btn"
                                    >
                                        <Brain className="w-4 h-4 mr-2" />
                                        Ask BrainSync
                                    </Button>
                                </motion.div>
                            ) : selectedCategory ? (
                                <motion.div
                                    className="text-center py-16"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                >
                                    <div className="w-16 h-16 rounded-2xl bg-secondary mx-auto mb-4 flex items-center justify-center">
                                        <Bookmark className="w-8 h-8 text-muted-foreground" />
                                    </div>
                                    <h3 className="font-heading text-lg font-semibold mb-2">No bookmarks here</h3>
                                    <p className="text-muted-foreground text-sm mb-6">Add bookmarks to this category</p>
                                    <Button onClick={() => setAddModalOpen(true)} className="rounded-full px-6 bg-primary" data-testid="empty-add-btn">
                                        <Plus className="w-4 h-4 mr-2" />
                                        Add Bookmark
                                    </Button>
                                </motion.div>
                            ) : loading ? (
                                // A refetch is in flight (e.g. right
                                // after clearing a no-results search).
                                // Render nothing rather than dropping
                                // into FirstRunWelcome — the skeleton
                                // above the grid already communicates
                                // the loading state.
                                null
                            ) : (
                                <FirstRunWelcome
                                    userName={user?.name}
                                    onQuickAdd={(url) => {
                                        setAddModalInitialUrl(url);
                                        setAddModalInitialMode('url');
                                        setAddModalOpen(true);
                                    }}
                                    onOpenAdd={() => setAddModalOpen(true)}
                                    onStarterSaved={() => {
                                        // Refresh the bookmark list so the
                                        // newly-cloned starter shows up in
                                        // the dashboard grid.
                                        fetchBookmarks();
                                    }}
                                />
                            )
                        ) : (
                            (() => {
                                const gridNode = (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
                                        {sortedBookmarks.map((bookmark, index) => {
                                            const isProcessing = bookmark.status === 'pending' || bookmark.status === 'processing';
                                            // Suppress in-feed ads when the curator
                                            // has set a manual order - an ad slot
                                            // would muddy the ranked sequence.
                                            // Also suppress for allowlisted brand
                                            // accounts (verified official pages).
                                            const showAd = !manualOrderActive
                                                && subscriptionInfo.tier === 'free'
                                                && !subscriptionInfo.ads_exempt
                                                && index > 0
                                                && index % 8 === 0;
                                            return (
                                            <React.Fragment key={bookmark.bookmark_id}>
                                                {showAd && <InFeedAd />}
                                                <SortableCard
                                                    id={bookmark.bookmark_id}
                                                    disabled={!manualOrderActive}
                                                    rank={index + 1}
                                                    showRank={showNumbering}
                                                >
                                        <BookmarkCard
                                            bookmark={bookmark}
                                            isProcessing={isProcessing}
                                            hasReminder={!!bookmarkReminders[bookmark.bookmark_id]}
                                            categories={categories}
                                            onNavigate={navigateToBookmark}
                                            onReprocess={handleReprocess}
                                            onToggleFavorite={handleToggleFavorite}
                                            onShare={handleShareBookmark}
                                            onDelete={handleDeleteBookmark}
                                            onUpgradeRequest={() => setUpgradeModalOpen(true)}
                                        />
                                                </SortableCard>
                                            </React.Fragment>
                                            );
                                        })}
                                    </div>
                                );
                                return manualOrderActive ? (
                                    <DndContext
                                        sensors={dndSensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={handleReorderDragEnd}
                                    >
                                        <SortableContext
                                            items={sortedBookmarks.map(b => b.bookmark_id)}
                                            strategy={rectSortingStrategy}
                                        >
                                            {gridNode}
                                        </SortableContext>
                                    </DndContext>
                                ) : gridNode;
                            })()
                        )
                        );
                        })()}

                        {/* Infinite scroll sentinel */}
                        {hasMore && (
                            <div ref={loadMoreRef} className="flex justify-center pt-6 pb-2" data-testid="load-more-sentinel">
                                {loadingMore && (
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* Add Bookmark Modal */}
            <AddBookmarkModal
                open={addModalOpen}
                onOpenChange={(v) => {
                    setAddModalOpen(v);
                    if (!v) {
                        setAddModalInitialUrl('');
                        setAddModalInitialMode('url');
                        setAddModalInitialFile(null);
                    }
                }}
                categories={categories}
                getCategoryIcon={getCategoryIcon}
                onBookmarkAdded={handleBookmarkAdded}
                onCategoryCreated={(newCat) => {
                    // Merge the new category into the shared array so the
                    // sidebar + every other CategoryPicker instance picks
                    // it up immediately without an extra round-trip.
                    setCategories((prev) => {
                        if (prev.some(c => c.category_id === newCat.category_id)) return prev;
                        const next = [...prev, newCat];
                        next.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                        return next;
                    });
                }}
                onSessionExpired={() => {
                    import('sonner').then(({ toast }) => {
                        toast.error('Session expired - please sign in again');
                    });
                    login();
                }}
                onLimitReached={handleLimitReached}
                onUpgradeRequest={() => setUpgradeModalOpen(true)}
                initialUrl={addModalInitialUrl}
                initialMode={addModalInitialMode}
                initialFile={addModalInitialFile}
            />

            {/* Upgrade Modal */}
            <UpgradeModal
                open={upgradeModalOpen}
                onClose={() => setUpgradeModalOpen(false)}
                onPurchaseComplete={() => {
                    // RC webhook may take a few seconds - poll subscription
                    // status until tier flips off "free", or give up after 20s.
                    const start = Date.now();
                    const poll = async () => {
                        await fetchSubscriptionStatus();
                        if (Date.now() - start > 20000) return;
                        // Re-fetch after 2s, 5s, 10s, 15s
                        setTimeout(poll, 3500);
                    };
                    poll();
                }}
                currentTier={subscriptionInfo.tier}
                bookmarkCount={subscriptionInfo.bookmark_count ?? totalCount}
                bookmarkLimit={subscriptionInfo.premium_limit ?? subscriptionInfo.bookmark_limit ?? 20}
                premiumUsed={subscriptionInfo.premium_used ?? 0}
                premiumLimit={subscriptionInfo.premium_limit ?? subscriptionInfo.bookmark_limit ?? 20}
            />

            {/* V4 Quota Threshold Nudge — fires when Free/Plus user crosses 80%
                on any quota (Premium AI saves, Ask, video-min, storage). One
                pop-in per quota-kind per 7 days, dismissible. */}
            <QuotaThresholdNudge
                subscriptionInfo={subscriptionInfo}
                onUpgrade={() => setUpgradeModalOpen(true)}
            />

            {/* AI Usage Stats Modal - value-proof panel, opened from the quota progress bar */}
            <AIUsageStatsModal
                open={aiUsageStatsOpen}
                onClose={() => setAiUsageStatsOpen(false)}
                onUpgrade={() => setUpgradeModalOpen(true)}
                subscriptionInfo={subscriptionInfo}
            />

            {/* Share Modal - used for both bookmarks and categories */}
            <ShareModal
                open={!!shareModalItem}
                onClose={() => setShareModalItem(null)}
                kind={shareModalItem?.kind || 'bookmark'}
                item={shareModalItem?.item}
                currentUser={user}
                onVisibilityChange={(v) => {
                    // Reflect the new visibility in the local bookmarks list so the
                    // dashboard UI updates immediately (e.g. "Shared" badge).
                    if (shareModalItem?.kind === 'bookmark' && shareModalItem?.item?.bookmark_id) {
                        setBookmarks(prev => prev.map(b =>
                            b.bookmark_id === shareModalItem.item.bookmark_id
                                ? { ...b, share_visibility: v }
                                : b
                        ));
                        setShareModalItem(prev => prev ? { ...prev, item: { ...prev.item, share_visibility: v } } : prev);
                    }
                }}
            />

            {/* Referrals - invite friends modal */}
            <ReferralsModal
                open={referralsModalOpen}
                onClose={() => setReferralsModalOpen(false)}
                currentUser={user}
            />

            {/* Public profile / username settings */}
            <UsernameSettingsModal
                open={usernameModalOpen}
                onOpenChange={setUsernameModalOpen}
            />

            {/* Share-from-any-app refresher - opened from the user dropdown
                so existing users who skipped the first-run onboarding can
                still discover this workflow. */}
            <ShareDemoRefresherModal
                open={shareDemoModalOpen}
                onOpenChange={setShareDemoModalOpen}
            />

            {/* Category Modal */}
            <CategoryModal
                open={categoryModalOpen}
                onOpenChange={(open) => { setCategoryModalOpen(open); if (!open) resetCategoryForm(); }}
                editingCategory={editingCategory}
                categoryName={categoryName}
                setCategoryName={setCategoryName}
                categoryDescription={categoryDescription}
                setCategoryDescription={setCategoryDescription}
                categoryShareVisibility={categoryShareVisibility}
                setCategoryShareVisibility={setCategoryShareVisibility}
                categoryIcon={categoryIcon}
                setCategoryIcon={setCategoryIcon}
                categoryColor={categoryColor}
                setCategoryColor={setCategoryColor}
                categoryParentId={categoryParentId}
                setCategoryParentId={setCategoryParentId}
                categoryManualOrder={categoryManualOrder}
                setCategoryManualOrder={setCategoryManualOrder}
                categoryShowNumbering={categoryShowNumbering}
                setCategoryShowNumbering={setCategoryShowNumbering}
                iconPickerOpen={iconPickerOpen}
                setIconPickerOpen={setIconPickerOpen}
                colorPickerOpen={colorPickerOpen}
                setColorPickerOpen={setColorPickerOpen}
                categories={categories}
                handleSaveCategory={handleSaveCategory}
                submitting={submitting}
                resetCategoryForm={resetCategoryForm}
            />

            {/* Delete Category Confirmation */}
            <DeleteCategoryDialog
                pendingId={deleteCategoryId}
                onCancel={() => setDeleteCategoryId(null)}
                onConfirm={handleDeleteCategory}
            />

            <ShareCategoryModal
                open={shareCategoryOpen}
                onOpenChange={setShareCategoryOpen}
                category={shareCategoryTarget}
                userTier={subscriptionInfo.tier}
                onRequestEdit={(cat) => {
                    // Bridge between Share modal and Edit modal - the
                    // Share modal closes itself first (so we don't
                    // stack two dialogs), then we open Edit with the
                    // same category pre-filled. One canonical place to
                    // change collection attributes (privacy included).
                    if (cat) openEditCategory(cat);
                }}
                onVisibilityChange={(newVis) => {
                    if (!shareCategoryTarget?.category_id) return;
                    setCategories(prev => prev.map(c =>
                        c.category_id === shareCategoryTarget.category_id
                            ? { ...c, share_visibility: newVis }
                            : c
                    ));
                    // Keep the modal's `category` prop fresh too so the
                    // selected radio sticks if the user reopens it.
                    setShareCategoryTarget(prev => prev ? { ...prev, share_visibility: newVis } : prev);
                }}
            />

            <OnboardingOverlay />
        </div>
    );
};

export default Dashboard;

